/**
 * English — the source of truth (docs/V2_I18N_PLAN.md). Every other locale's dictionary is
 * `Record<TranslationKey, string>` (`TranslationKey = keyof typeof en`, see `i18n/index.ts`), so
 * `tsc` fails the moment a translation is missing or stale — this file's own key set IS the contract.
 *
 * Flat, dot-namespaced keys (`"editor.status.saved"`), not nested objects — keeps `t()` a single
 * property lookup and keeps a diff of one string a one-line diff. Namespaced per page/component
 * (`login.*`, `editor.*`, `presetMenu.*`, `profile.*`, `common.*` for the handful of words reused
 * verbatim across unrelated components — Cancel/Delete/Save-in-flight labels).
 *
 * NOT here (docs/V2_I18N_PLAN.md "NOT localized"): rider/preset user content, catalog preset keys
 * (`VOC_STAND_STRAIGHT` etc.), API-returned error bodies, console/log strings, brand names
 * ("SoundCheckBox", "Pro Tools", "SCB").
 */
export const en = {
  // --- common — short words/phrases reused verbatim across unrelated components ------------------
  "common.brandHomeAriaLabel": "SoundCheckBox — home",
  "common.close": "Close",
  "catalogCard.open": "Catalogue card",
  "catalogCard.openAlternatives": "Catalogue cards for the alternatives",
  "catalogCard.alternatives": "Alternatives",
  "catalogCard.type": "Type",
  "catalogCard.category": "Category",
  "catalogCard.weight": "Weight",
  "catalogCard.power": "Power",
  "about.heading": "About this build",
  "about.web": "Web",
  "about.api": "API",
  "about.schema": "Schema contract",
  "about.unknown": "unknown",
  "about.unstamped": "unstamped build",
  "about.envProduction": "Production",
  "about.envNotProduction": "{env} — not production",
  "about.mismatch": "The web bundle and the API were built from different commits. A deploy landed only halfway: what you see and what answers you are not the same code.",
  "common.cancel": "Cancel",
  "common.delete": "Delete",
  "ridersList.deletePublished.heading": "This rider is published",
  "ridersList.deletePublished.body": "Deleting it also takes its public link down. Anyone holding that link — a venue, a rental company, whoever scanned the printed QR — will stop being able to open it, and it cannot be restored.",
  "ridersList.deletePublished.dontAskAgain": "Don't ask again in this session",
  "common.deletePermanentlyPrompt": "Delete permanently?",
  "common.yesDelete": "Yes, delete",
  "common.loading": "Loading…",
  "common.saving": "Saving…",
  "common.sending": "Sending…",

  // --- side-nav (components/side-nav.ts) ----------------------------------------------------------
  "sideNav.home": "Home",
  "sideNav.soundman": "Soundman",
  "sideNav.soundmanComingSoon": "Soundman — coming soon",
  "sideNav.myRiders": "My Riders",
  "sideNav.offers": "Offers",
  "myEquipment.warehouse.heading": "My warehouse",
  "myEquipment.warehouse.search": "Search my warehouse",
  "myEquipment.warehouse.add": "Add to my warehouse",
  "myEquipment.warehouse.formed": "My warehouse is formed — offers suggest from it first",
  "myEquipment.backToOffer": "← Back to the offer",
  "myEquipment.fromOffer.line": "From {offer} on the rider {rider}, added to your warehouse: {items}.",
  "myEquipment.fromOffer.grow": "Grow your warehouse:",
  "myEquipment.fromOffer.pick": "Pick from our catalogue",
  "myEquipment.fromOffer.import": "Import your list",
  "myEquipment.fromOffer.dismiss": "Hide",
  "myEquipment.weight.notSpecified": "Not specified",
  "myEquipment.col.manufacturer": "Manufacturer",
  "myEquipment.sort.by": "Sort by",
  "myEquipment.sort.category": "Category",
  "myEquipment.more": "More",
  "myEquipment.searchClose": "Close search",
  "sideNav.inbox": "Inbox",
  "sideNav.inboxComingSoon": "Inbox — coming soon",
  "sideNav.outbox": "Outbox",
  "sideNav.outboxComingSoon": "Outbox — coming soon",
  "sideNav.eventManager": "Event manager",
  "sideNav.eventManagerComingSoon": "Event manager — coming soon",
  "sideNav.warehouse": "Warehouse",
  "sideNav.warehouseComingSoon": "Warehouse — coming soon",
  "sideNav.myProfile": "My Profile",
  "sideNav.admin": "Administration",
  "sideNav.designThemeLabel": "Design theme",
  "sideNav.languageLabel": "Language",
  "sideNav.mainAriaLabel": "Main",

  // --- app-header (components/app-header.ts) ------------------------------------------------------
  "appHeader.adminTitle": "Administrator",
  "appHeader.adminBadge": "admin",
  "appHeader.logoutTitle": "Log out",
  "appHeader.logoutLabel": "Logout",

  // --- verify-email banner (components/verify-email-banner.ts) -------------------------------------
  "verifyBanner.message": "Your email isn't confirmed yet — saving, publishing and other changes are disabled until you verify it.",
  "verifyBanner.profileLink": "Go to profile",
  "verifyBanner.resend": "Resend confirmation email",
  "verifyBanner.resendSent": "Sent ✓",
  "verifyBanner.resendFailed": "Couldn't resend the confirmation email — try again from your profile page.",
  "verifyBanner.dismissAriaLabel": "Dismiss",

  // --- write errors shown in place of a raw API error (api/http.ts#isEmailNotVerifiedError) --------
  "errors.emailNotVerified": "Verify your email before making changes — go to your profile to resend the link.",

  // --- login (pages/login-page.ts) -----------------------------------------------------------------
  "login.hint": "Log in or create an account to build and save technical riders.",
  "login.hintRegister": "Create an account — it takes an email and a password, and your work comes with you.",
  "login.emailLabel": "Email or login",
  "login.passwordLabel": "Password",
  "login.submit": "Log in",
  "login.register": "Register",
  "login.consentNotice": "By creating an account you agree to our {termsLink} and {privacyLink}.",
  "login.termsLink": "Terms of Use",
  "login.privacyLink": "Privacy Policy",
  "legal.privacyTitle": "Privacy Policy",
  "legal.termsTitle": "Terms of Use",
  "legal.unavailable": "This document could not be loaded. Please try again later.",
  "consent.title": "Please review our terms",
  "consent.body": "To keep using SoundCheckBox, please agree to our {termsLink} and {privacyLink}.",
  "consent.changeNote": "We may update these documents. If you ever disagree with a change, you can stop using the service.",
  "consent.agree": "I agree",
  "consent.decline": "Sign out",
  "consent.saveFailed": "We could not record your agreement. Please try again.",
  "editor.publishConfirm": "This rider will be published at a public link that anyone can open — no account needed, and it stays available until you unpublish it. Anything written in it, including names, phone numbers and email addresses, becomes public too. Publish it?",
  "editor.publishImpact.offer": "This rider is in an offer that is not agreed yet ({shows}). Publishing a new version will change that offer — the supplier will answer the changed lines again. Publish?",
  "editor.publishImpact.contract": "This rider is under an agreed offer ({shows}). It cannot be changed there without changing the show's contract: the supplier will have to confirm the difference, and the crew will see the new version. Publish?",
  "editor.publishImpact.unnamedShow": "a show with no name",
  "editor.unpublishConfirm": "Unpublishing takes down EVERY public link for this rider — this version and every earlier one — and anyone you have already sent a link to will stop being able to open it. This cannot be undone; publishing again creates a NEW link. Unpublish it?",
  "editor.publishDirty.heading": "Publish unsaved changes?",
  "editor.publishDirty.body": "Publishing uses the last saved version of this rider, and your latest changes are not saved yet. Save them first to publish them.",
  "editor.publishDirty.saveAndPublish": "Save and publish",
  "login.minAge": "You must be at least 16 years old to create an account.",
  "admin.title": "Admin console",
  "admin.notAvailable": "This page is only available to administrators.",
  "admin.users.title": "Users",
  "admin.users.searchPlaceholder": "Search by email or login",
  "admin.users.searchBtn": "Search",
  "admin.users.showAdmins": "Show administrators",
  "admin.users.browseAll": "Browse all",
  "admin.users.prevPage": "Previous",
  "admin.users.nextPage": "Next",
  "admin.users.pageOf": "Page {page} of {pages} — {total} accounts",
  "admin.users.searchTooShort": "Enter at least 2 characters.",
  "admin.users.noMatch": "No accounts match.",
  "admin.users.colEmail": "Email",
  "admin.users.colLogin": "Login",
  "admin.users.colJoined": "Joined",
  "admin.users.colVerified": "Verified",
  "admin.users.colAdmin": "Admin",
  "admin.users.colActions": "Actions",
  "admin.users.verify": "Mark verified",
  "admin.users.grantAdmin": "Make admin",
  "admin.users.revokeAdmin": "Revoke admin",
  "admin.users.verified": "Email marked as verified.",
  "admin.users.granted": "Administrator access granted.",
  "admin.users.revoked": "Administrator access revoked.",
  "admin.users.confirmGrant": "Give administrator access to {user}?",
  "admin.users.confirmRevoke": "Remove administrator access from {user}?",
  "admin.users.deletionPending": "deletion requested",
  "admin.users.tempPassword": "Temporary password",
  "admin.users.tempPasswordPending": "temporary password",
  "admin.users.confirmTempPassword": "Issue a temporary password for {user}? Their current password stops working immediately.",
  "admin.users.tempPasswordIssued": "Temporary password for {user}. Copy it now — it cannot be shown again:",
  "admin.users.tempPasswordDone": "Temporary password issued.",
  // --- users tab: sort, filters, summary (SCRUM-570) ---
  "admin.users.colLastLogin": "Last login",
  "admin.users.colRiders": "Riders",
  "admin.users.colLastRiderEdit": "Last rider edit",
  "admin.users.sortBy": "Sort by {column}",
  "admin.users.noLoginRecorded": "Not recorded. Logins have been tracked only since October 2026 — before that nothing was stored, so this does not mean the account was never used.",
  "admin.users.noRiderEdit": "No riders on this account, so there is no edit to date.",
  "admin.users.filtersTitle": "Filters",
  "admin.users.filterAll": "All",
  "admin.users.filterRole": "Role",
  "admin.users.filterRoleUsers": "Users",
  "admin.users.filterRoleAdmins": "Admins",
  "admin.users.filterVerified": "Email verified",
  "admin.users.filterVerifiedYes": "Verified",
  "admin.users.filterVerifiedNo": "Not verified",
  "admin.users.filterRiders": "Riders",
  "admin.users.filterRidersWith": "With riders",
  "admin.users.filterRidersWithout": "Without riders",
  "admin.users.filterActiveDays": "Edited a rider in the last … days",
  "admin.users.filterJoinedDays": "Registered in the last … days",
  "admin.users.filterAnyDays": "any",
  "admin.users.clearFilters": "Clear filters",
  "admin.stats.title": "Summary",
  "admin.stats.filteredHint": "Counts cover the accounts matching the filters above.",
  "admin.stats.unfilteredHint": "Counts cover every account.",
  "admin.stats.total": "Accounts",
  "admin.stats.admins": "Admins",
  "admin.stats.verified": "Verified",
  "admin.stats.withRiders": "With riders",
  "admin.stats.ridersTotal": "Riders in total",
  "admin.stats.windowHeader": "In the last…",
  "admin.stats.days7": "7 days",
  "admin.stats.days30": "30 days",
  "admin.stats.days90": "90 days",
  "admin.stats.days365": "365 days",
  "admin.stats.activeRiderAuthors": "Edited a rider",
  "admin.stats.newUsers": "Registered",
  "admin.stats.loggedIn": "Logged in",
  "admin.stats.loginNote": "Logins have been recorded only since October 2026. The “Logged in” row counts from that day; earlier sessions are not in it.",
  "admin.erasure.title": "Accounts due for erasure",
  "admin.erasure.hint": "Deletion requests whose waiting period has elapsed. Nothing is erased automatically — erasing is irreversible and removes the account with all of its riders.",
  "admin.erasure.none": "Nothing is due.",
  "admin.erasure.colRequested": "Requested",
  "admin.erasure.colDue": "Due since",
  "admin.erasure.erase": "Erase",
  "admin.erasure.confirmPrompt": "Type {user} to erase this account and all of its riders. This cannot be undone.",
  "admin.erasure.confirmMismatch": "That address doesn't match — nothing was erased.",
  "admin.erasure.erased": "{user} has been erased.",
  "admin.content.legalTitle": "Legal documents",
  "admin.content.legalHint": "Replaces the document shipped with the site. Empty means the file in the repository is used, exactly as before. Markdown.",
  "admin.content.mailTitle": "Email texts",
  "admin.content.mailHint": "Replaces individual strings in transactional email, per language. The field holds the text that is live now — edit it in place. Saving it unchanged keeps the shipped version; \"Revert\" drops your edit. Placeholders in braces must be kept as they are — they are filled in when the message is sent.",
  "admin.content.localeLabel": "Language",
  "admin.content.usingFile": "shipped version",
  "admin.content.edited": "edited here",
  "admin.content.legalPlaceholder": "Empty — the site is showing the document from the repository.",
  "admin.content.colKey": "Key",
  "admin.content.colText": "Text",
  "admin.content.save": "Save",
  "admin.content.revert": "Revert",
  "admin.content.saved": "Saved.",
  "admin.content.reverted": "Reverted to the shipped version.",
  "admin.content.confirmRevert": "Discard this edit and go back to the shipped document?",
  "admin.content.uiTitle": "Interface texts",
  "admin.content.uiHint": "Replaces individual interface strings, per language, without a rebuild. The field holds the text that is live now — edit it in place. Saving it unchanged keeps the shipped version; \"Revert\" drops your edit. Changes appear for everyone after a page reload.",
  "admin.content.uiSearchPlaceholder": "Search by key or text",
  "admin.content.uiEditedOnly": "only edited",
  "admin.content.uiStart": "Type something to search, or tick “only edited”.",
  "admin.content.uiTooMany": "{count} matches — showing the first 200. Narrow the search.",
  "admin.content.badPlaceholders": "Unknown placeholders: {list}. They would be printed literally.",
  "admin.content.savedReload": "Saved. Reload the page to see it.",
  "admin.tabs.users": "Accounts",
  "admin.tabs.content": "Texts",
  "admin.tabs.settings": "Settings",
  "admin.content.history": "History",
  "admin.content.noHistory": "No earlier versions yet.",
  "admin.content.restore": "Load this version",
  "admin.content.restoredIntoEditor": "Loaded into the editor — check it, then save.",
  "admin.settings.title": "Site settings",
  "admin.settings.hint": "Changes take effect immediately. A setting with no override uses the value the code ships with.",
  "admin.settings.save": "Save",
  "admin.settings.reset": "Reset to default",
  "admin.settings.saved": "Saved {key}.",
  "admin.settings.wasReset": "{key} is back to its default.",
  "admin.settings.overridden": "changed from the default ({default})",
  "admin.settings.isDefault": "default",
  "admin.models.title": "Unused 3D models",
  "admin.models.hint": "Uploaded .glb files that no preset and no saved rider points at. Deleting one is permanent — there is no upload history to restore from.",
  "admin.models.scan": "Scan",
  "admin.models.scanning": "Scanning…",
  "admin.models.none": "Nothing to clean up — every uploaded model is in use.",
  "admin.models.summary": "{count} unused, {size} total. {inUse} still in use. Scanned {presets} presets and {riders} riders.",
  "admin.models.colFile": "File",
  "admin.models.colSize": "Size",
  "admin.models.colModified": "Uploaded",
  "admin.models.deleteSelected": "Delete selected",
  "admin.models.deleteSelectedCount": "Delete selected ({count})",
  "admin.models.confirmDelete": "Permanently delete {count} model file(s)? This cannot be undone.",
  "admin.models.deleted": "Deleted {count} file(s), freed {size}.",
  "admin.models.skipped": "{count} skipped — they are referenced now.",
  "login.forgotPassword": "Forgot your password?",
  "login.sessionExpired": "Your session has expired — please sign in again.",
  // Anti-enumeration: shown for ANY wrong-password/wrong-identifier 401 — never reveals whether
  // the account exists (apps/web/src/pages/login-page.ts).
  "login.invalidCredentials": "Incorrect email/login or password.",

  // --- recovery (pages/recovery-pages.ts) -----------------------------------------------------------
  "recovery.forgotTitle": "Reset your password",
  "recovery.forcedTitle": "Choose your own password",
  "recovery.forcedLead": "You signed in with a password issued by support. Set your own to continue — nothing else is available until you do.",
  "recovery.forcedCurrentLabel": "Temporary password",
  "recovery.forcedSubmit": "Save password",
  "recovery.forgotLead": "Enter your email and we'll send you a link to choose a new password.",
  "recovery.emailLabel": "Email",
  "recovery.forgotSubmit": "Send reset link",
  "recovery.backToSignIn": "Back to sign in",
  "recovery.forgotSuccess": "If that address has an account, a reset link is on its way. Check your inbox and spam folder.",
  "recovery.resetIncompleteTitle": "Reset link is incomplete",
  "recovery.resetIncompleteLead": "This link is missing its token. Request a fresh one.",
  "recovery.resetAgainLink": "Request a new reset link",
  "recovery.resetTitle": "Choose a new password",
  "recovery.newPasswordLabel": "New password",
  "recovery.repeatPasswordLabel": "Repeat new password",
  "recovery.resetSubmit": "Set new password",
  "recovery.passwordMismatch": "The two passwords don't match.",
  "recovery.resetSuccess": "Password updated. Redirecting you to sign in…",
  "recovery.verifyTitle": "Confirming your email",
  "recovery.verifyChecking": "Checking your link…",
  "recovery.continueToSignIn": "Continue to sign in",
  "recovery.verifyNoToken": "This link is missing its token. Request a new one from your profile page.",
  "recovery.verifySuccess": "Your email is confirmed. You can sign in now.",
  "recovery.verifyFailed": "This confirmation link is invalid or has expired. Sign in and request a new one from your profile.",
  "recovery.verifyChangeTitle": "Confirming your new email",
  "recovery.verifyChangeNoToken": "This link is missing its token. Request a new one from your profile page.",
  "recovery.verifyChangeSuccess": "Your new email address is confirmed — sign in with it from now on.",
  "recovery.verifyChangeFailed": "This confirmation link is invalid or has expired. Sign in and request a new email change from your profile.",

  // --- landing (pages/landing-page.ts) --------------------------------------------------------------
  "landing.nav.try": "Try",
  "landing.nav.eventManager": "For Event Manager",
  "landing.nav.rentCompany": "For Rent Company",
  "landing.nav.soundman": "For Soundman",
  "landing.nav.howItWorks": "How it works",
  "landing.nav.contacts": "Contacts",
  "landing.nav.myRiders": "My riders",
  "landing.nav.login": "Log in",
  "landing.nav.signup": "Sign up free",
  "landing.hero.title": "Set up your show",
  "landing.hero.sub": "Online service to get any gear you need for the show. Build technical rider with all the details and find rental companies in one click.",
  "landing.hero.cta": "Open the editor",
  "landing.hero.mediaAlt": "The SoundCheckBox 3D rider editor",
  "landing.try.title": "Build a rider right now",
  "landing.try.lead": "No account needed. The editor opens straight away and your work stays in this browser — an account is only for keeping it, sharing it or sending it.",
  "landing.try.cta": "Open the editor",
  "landing.eventManager.title": "For event managers",
  "landing.eventManager.body":
    "Managing a festival, a tour, a corporate or a private event? SoundCheckBox is the assistant that keeps band and rental agreements in one place — so you always know what was promised, by whom, and for how much.",
  "landing.eventManager.feature1Title": "Equipment provision",
  "landing.eventManager.feature1Desc": "Every rider request matched against what suppliers can actually deliver.",
  "landing.eventManager.feature2Title": "Selection options",
  "landing.eventManager.feature2Desc": "Compare variants side by side instead of chasing quotes over email.",
  "landing.eventManager.feature3Title": "Agreement tracking",
  "landing.eventManager.feature3Desc": "Company credibility and past event reviews, visible before you commit.",
  "landing.eventManager.feature4Title": "Payment processing",
  "landing.eventManager.feature4Desc": "Settle the booking without leaving the platform.",
  "landing.eventManager.mediaAlt": "Event manager workflow",
  "landing.rentCompany.title": "For rental companies",
  "landing.rentCompany.body":
    "An automated manager that reads an incoming rider, checks it against your inventory, and prices the job — automatically. Short of any gear? SoundCheckBox will direct you where to rent it.",
  "landing.rentCompany.feature1Title": "Inventory matching",
  "landing.rentCompany.feature1Desc": "Riders are compared to your real warehouse stock, not a wish list.",
  "landing.rentCompany.feature2Title": "Automatic pricing",
  "landing.rentCompany.feature2Desc": "Quotes calculated from your own rates the moment a rider arrives.",
  "landing.rentCompany.feature3Title": "Large operations",
  "landing.rentCompany.feature3Desc": "Built for serious storage volumes, with custom software integration supported.",
  "landing.rentCompany.feature4Title": "Sub-rental",
  "landing.rentCompany.feature4Desc": "Missing an item? Get pointed straight at a company that has it.",
  "landing.rentCompany.mediaAlt": "Rental company workflow",
  "landing.soundman.title": "For soundmen",
  "landing.soundman.body":
    "Less paperwork, more time for the people you actually work with. Build a rider from setups that already work, and reuse them for every band you look after.",
  "landing.soundman.feature1Title": "Proven setups",
  "landing.soundman.feature1Desc": "Start from a working stage plot instead of a blank page.",
  "landing.soundman.feature2Title": "Your own presets",
  "landing.soundman.feature2Desc": "Save a customised group of gear once, drop it in forever.",
  "landing.soundman.feature3Title": "Multi-band booking",
  "landing.soundman.feature3Desc": "Keep every band you manage in one account.",
  "landing.soundman.feature4Title": "Package suggestions",
  "landing.soundman.feature4Desc": "Rental companies propose complete packages against your rider.",
  "landing.soundman.mediaAlt": "Soundman workflow",
  "landing.howItWorks.videoTitle": "SoundCheckBox — how it works",
  "landing.howItWorks.lead":
    "Place the gear on a 3D stage, and the input list, output list and equipment list build themselves as you go. Print it, or send it straight to a rental company.",
  "landing.howItWorks.feature1Title": "1. Build the stage",
  "landing.howItWorks.feature1Desc": "Drag presets — drum kits, backline, mics, monitors — onto a scaled stage.",
  "landing.howItWorks.feature2Title": "2. Lists write themselves",
  "landing.howItWorks.feature2Desc": "Channels, sources and gear are derived from what's on the stage.",
  "landing.howItWorks.feature3Title": "3. Share it",
  "landing.howItWorks.feature3Desc": "Print a clean A4 rider with the stage plot, or send it to a supplier.",
  "landing.contact.title": "Contact us",
  "landing.contact.emailLabel": "Email",
  "landing.contact.addressLabel": "Address",
  "landing.contact.followUs": "Follow us",
  "landing.contact.nameFieldLabel": "Your name",
  "landing.contact.emailFieldLabel": "Your email",
  "landing.contact.messageLabel": "Message",
  "landing.contact.submit": "Send message",
  "landing.contact.captchaNote": "Protected by Cloudflare Turnstile — Cloudflare's {privacyLink} and {termsLink} apply.",
  "landing.contact.privacyPolicyLinkText": "Privacy Policy",
  "landing.contact.termsLinkText": "Terms of Service",
  "landing.contact.validationError": "Please fill in your name, email and a message.",
  "landing.contact.success": "Thanks — your message is on its way. We'll reply to the address you gave us.",
  "landing.footer.tagline": "SoundCheckBox — technical rider constructor",

  // --- riders list (pages/riders-list-page.ts) ------------------------------------------------------
  "riders.pageTitle": "My riders",
  "riders.newRiderButton": "New Rider",
  "riders.filter.groupLabel": "Filter riders by status",
  "riders.filter.all": "All",
  "riders.filter.empty": "No riders match this filter.",
  "riders.table.title": "Title",
  "riders.table.band": "Band / Artist",
  "riders.table.created": "Created",
  "riders.table.changed": "Changed",
  "riders.table.status": "Status",
  "riders.table.action": "Action",
  "riders.table.empty": "No riders yet — click “+ New Rider” to create your first one.",
  "riders.status.published": "Published",
  "riders.status.draft": "Draft",
  "riders.status.publicLinkTitle": "Public link: {url}",
  "riders.actions.edit": "Edit",
  "riders.actions.moreActionsAriaLabel": "More actions for {title}",
  "riders.actions.publish": "Publish",
  "riders.actions.unpublish": "Unpublish",
  "riders.actions.print": "Print",
  "riders.actions.copyPublicLink": "Copy public link",
  "riders.actions.rename": "Rename",
  "riders.actions.duplicate": "Duplicate",
  "riders.copySuffix": "(copy)",
  "riders.copySuffixN": "(copy {n})",
  "riders.rename.heading": "Rename rider",
  "riders.rename.nameLabel": "New name",
  "riders.rename.confirm": "Rename",
  "riders.status.renaming": 'Renaming "{title}"…',
  "riders.status.renamed": 'Renamed to "{title}".',
  "riders.status.renameFailed": 'Failed to rename "{title}": {message}',
  "riders.status.duplicating": 'Duplicating "{title}"…',
  "riders.status.duplicated": 'Created "{title}".',
  "riders.status.duplicateFailed": 'Failed to duplicate "{title}": {message}',
  "riders.status.deleting": 'Deleting "{title}"…',
  "riders.status.deleted": 'Deleted "{title}".',
  "riders.status.deleteFailed": 'Failed to delete "{title}": {message}',
  "riders.status.linkCopied": "Public link copied: {url}",
  "riders.status.unpublished": 'Unpublished "{title}".',
  "riders.status.publishSuccess": 'Published "{title}" — public link: {url}',
  "riders.status.unpublishFailed": 'Failed to unpublish "{title}": {message}',
  "riders.status.publishFailed": 'Failed to publish "{title}": {message}',
  "riders.status.publishedNoPicture": "The public page has no stage plan picture: this list cannot draw one. Publish from the editor to add it.",
  "riders.status.loading": "Loading your riders…",
  "riders.status.loadFailed": "Failed to load riders: {message}",

  // --- profile (pages/profile-page.ts) ---------------------------------------------------------------
  "profile.pageTitle": "My profile",
  "profile.personal.title": "Personal data",
  "profile.personal.nameLabel": "Name",
  "profile.personal.phoneLabel": "Phone",
  "profile.personal.countryLabel": "Country",
  "profile.personal.cityLabel": "City",
  "profile.personal.languageLabel": "Language",
  "profile.personal.save": "Save personal data",
  "profile.work.title": "Working data",
  "profile.work.companyLabel": "Company",
  "profile.work.roleLabel": "I am a",
  "roleChoice.hint": "Decides which page opens after you sign in.",
  "roleChoice.hintOptional": "Optional. Decides which page opens after you sign in.",
  "profile.work.role.soundman": "Sound engineer",
  "profile.work.role.manager": "Event manager",
  "profile.work.role.rental": "Rental company",
  "profile.work.role.other": "Other",
  "profile.work.save": "Save working data",
  "profile.defaults.title": "Rider defaults",
  "profile.defaults.hint": "These pre-fill the contact block of every NEW rider. Each rider keeps its own final values.",
  "profile.defaults.bandNameLabel": "Band name",
  "profile.defaults.contactNameLabel": "Contact name",
  "profile.defaults.contactPhoneLabel": "Contact phone",
  "profile.defaults.contactEmailLabel": "Contact email",
  "profile.defaults.save": "Save rider defaults",
  "profile.security.title": "Security",
  "profile.security.emailLabel": "Email",
  "profile.security.emailConfirmedLabel": "Email confirmed",
  "profile.security.passwordChangedLabel": "Password last changed",
  "profile.security.memberSinceLabel": "Member since",
  "profile.security.emailVerifiedYes": "Yes",
  "profile.security.emailVerifiedNot": "Not yet",
  "profile.security.resendVerification": "Resend confirmation email",
  "profile.security.changePasswordTitle": "Change password",
  "profile.security.changePasswordSubmit": "Change password",
  "profile.security.currentPasswordLabel": "Current password",
  "profile.security.newPasswordLabel": "New password",
  "profile.security.repeatPasswordLabel": "Repeat new password",
  "profile.security.resendSent": "Sent ✓",
  "profile.security.resendSuccess": "Confirmation email sent. Check your inbox and spam folder.",
  "profile.security.passwordMismatch": "The two new passwords don't match.",
  "profile.security.passwordChanged": "Password changed. We've emailed you a confirmation.",
  "profile.security.changeEmailTitle": "Change email",
  "profile.security.changeEmailHint": "We'll email a confirmation link to the new address — nothing changes until you click it.",
  "profile.security.newEmailLabel": "New email",
  "profile.security.currentPasswordForEmailLabel": "Current password",
  "profile.security.changeEmailSubmit": "Send confirmation link",
  "profile.security.changeEmailMissingFields": "Enter the new email address and your current password.",
  "profile.security.changePasswordMissingFields": "Enter your current password and a new password of at least 8 characters.",
  "profile.security.changeEmailSuccess": "If {newEmail} is available, a confirmation link is on its way there. Check its inbox and spam folder.",
  "profile.security.changeEmailPending": "We sent a confirmation link to {newEmail}. You're still logged in as {currentEmail} until it's confirmed.",
  "profile.ai.title": "AI assistant",
  "profile.ai.hint": "The assistant has a small free allowance. After that, add your own Google Gemini key to keep using it — you pay Google directly, with no limit here.",
  "profile.ai.usageFree": "Free allowance used: {used} of {cap}.",
  "profile.ai.usageByok": "Using your own key ({hint}) — no limit.",
  "profile.ai.byokHint": "Paste your own Gemini API key to keep using the assistant after the free allowance.",
  "profile.ai.getKeyLink": "Get a free key",
  "profile.ai.keyLabel": "Your Gemini API key",
  "profile.ai.keyReplace": "enter a new key to replace it",
  "profile.ai.saveKey": "Save key",
  "profile.ai.clearKey": "Remove key",
  "profile.ai.byokUnavailable": "Bring-your-own-key isn't available on this server yet.",
  "profile.ai.keyRequired": "Enter your API key first.",
  "profile.ai.keyValidating": "Checking the key…",
  "profile.ai.keySaved": "Key saved — the assistant now uses it.",
  "profile.ai.keyCleared": "Key removed.",
  "profile.ai.keySaveFailed": "Could not save the key: {message}",
  "profile.ai.loadFailed": "Could not load your AI usage.",
  "profile.privacy.title": "Data & privacy",
  "profile.privacy.hint": "Your rights under the GDPR and comparable laws.",
  "profile.privacy.downloadTitle": "Download my data",
  "profile.privacy.downloadHint": "Everything we store about you, including all your riders, as one JSON file.",
  "profile.privacy.downloadButton": "Download",
  "profile.privacy.marketingTitle": "Product emails",
  "profile.privacy.marketingHint":
    "Occasional news about SoundCheckBox. Service emails (password resets, confirmations) are always sent.",
  "profile.privacy.marketingCheckbox": "Send me product emails",
  "profile.privacy.deleteTitle": "Delete my account",
  "profile.privacy.deleteHint": "Permanently erases your account and every rider you own.",
  "profile.privacy.deleteButton": "Delete account",
  "profile.privacy.cancelDeleteButton": "Cancel deletion",
  "profile.status.loadFailed": "Couldn't load your profile: {message}",
  "profile.status.personalSaved": "Personal data saved.",
  "profile.status.workSaved": "Working data saved.",
  "profile.status.defaultsSaved": "Rider defaults saved.",
  "profile.privacy.preparingExport": "Preparing your data…",
  "profile.privacy.exportDownloaded": "Your data file has been downloaded.",
  "profile.privacy.marketingOn": "You'll receive product emails.",
  "profile.privacy.marketingOff": "You've been unsubscribed from product emails.",
  "profile.privacy.deleteConfirm":
    "Delete your SoundCheckBox account?\n\nYour account and every rider you own will be permanently erased after the grace period. You can cancel from this page before then.",
  "profile.privacy.deletionScheduled": "Deletion scheduled. We've emailed you the details — you can still cancel.",
  "profile.privacy.deletionCancelled": "Deletion cancelled. Your account stays active.",
  "profile.privacy.deletionPendingHint":
    "Scheduled for erasure on {date}. Cancel any time before then — nothing is removed until that date.",

  // --- public rider viewer (pages/public-viewer-page.ts) ---------------------------------------------
  "publicViewer.headerSub": "public rider",
  "publicViewer.riderListsAriaLabel": "Rider lists",
  "publicViewer.controls.grid": "Grid",
  "publicViewer.controls.titles": "Titles",
  "publicViewer.controls.inputCh": "Input (CH)",
  "publicViewer.controls.outputMix": "Output (MIX)",
  "publicViewer.controls.source": "Source",
  "publicViewer.controls.comments": "Comments",
  "publicViewer.window.close": "Close window",
  "publicViewer.window.zoomIn": "Increase scale",
  "publicViewer.window.zoomOut": "Decrease scale",
  "publicViewer.controls.scene": "3D stage",
  "publicViewer.controls.sceneLoading": "3D (loading...)",
  "publicViewer.controls.sceneHint": "Look at the stage in three dimensions — drag to orbit around it.",
  "publicViewer.controls.plan": "Plan view",
  "publicViewer.controls.planHint": "Look straight down at the stage, from the audience side.",
  "publicViewer.controls.schematic": "B&W Plan",
  "publicViewer.schematicLoading": "Drawing the sheet…",
  "publicViewer.schematicFailed": "The symbol library could not be loaded.",
  "publicViewer.controls.schematicHint": "High-contrast black-and-white stage diagram for print and quick reading.",
  "publicViewer.controls.schematicDisabledHint": "Not ready yet — the black-and-white plan is still being worked on.",
  "publicViewer.loaderAlt": "Loading data and 3D models...",
  "publicViewer.controls.hideStage": "Hide stage",
  "publicViewer.controls.showStage": "Show stage",
  "publicViewer.controls.hideStageHint": "Hide the stage and let the lists and the rider text take the whole screen.",
  "publicViewer.controls.viewSwitchAriaLabel": "Stage view",
  "publicViewer.controls.follow": "Zoom to selection",
  "publicViewer.controls.followHint": "Picking a channel flies the camera to the object it comes from.",
  "publicViewer.controls.labels": "Labels",
  "publicViewer.controls.fit": "Fit stage",
  "publicViewer.controls.fitHint": "Frame the whole stage again.",
  "publicViewer.controls.print": "Print",
  "publicViewer.stageHint": "Drag to orbit, pinch/scroll to zoom, tap an object to select it.",
  "publicViewer.stageHintPlan": "Drag to pan, pinch/scroll to zoom, tap an object to select it.",
  "publicViewer.planPosterAlt": "Stage plan rendered when this rider was published",
  "publicViewer.deselectAriaLabel": "Deselect",
  "publicViewer.tabs.rider": "Rider",
  "publicViewer.tabs.input": "Input",
  "publicViewer.tabs.output": "Output",
  "publicViewer.tabs.gear": "Gear",
  "publicViewer.bandLogoAlt": "Band logo",
  "publicViewer.table.ch": "Ch",
  "publicViewer.table.source": "Source",
  "publicViewer.table.type": "Type",
  "publicViewer.table.name": "Name",
  "publicViewer.table.comment": "Comment",
  "publicViewer.table.category": "Category",
  "publicViewer.table.owner": "Owner",
  "publicViewer.table.count": "Count",
  "publicViewer.footer": "Made with",
  "publicViewer.meta.inputCh": "Input CH",
  "publicViewer.meta.outputMix": "Output MIX",
  "publicViewer.meta.gear": "Gear",
  "publicViewer.meta.gearCount": "{n} item(s)",
  "publicViewer.noRiderNotes": "No rider notes.",
  "publicViewer.untitledRiderHeading": "Untitled rider",
  "publicViewer.untitledRiderDocTitle": "Rider",
  "publicViewer.documentTitle": "{title} — SoundCheckBox",
  "publicViewer.contactLabel": "Contact: {name}",
  "publicViewer.telLabel": "Tel: {phone}",
  "publicViewer.emailLabel": "Email: {email}",
  "publicViewer.status.noRiderId": "No rider specified — this link is missing its id.",
  "publicViewer.status.withdrawn": "This rider was withdrawn by its author, and this link no longer works.",
  "common.deletePublishedPrompt": "Delete permanently? Its public link stops working for everyone who has it.",
  "common.yesDeleteAndUnpublish": "Yes, delete and take the link down",
  "publicViewer.status.notPublished": "This rider isn't published (or doesn't exist). Ask its owner to publish it.",
  "publicViewer.powerUnavailable": "Power summary unavailable.",
  "publicViewer.controls.makeOffer": "Make offer",
  "publicViewer.controls.makeOfferHint": "Answer this rider's channel and equipment list as a supplier.",
  "publicViewer.controls.compact": "Compact view",
  "publicViewer.controls.compactHint": "The black-and-white plan with the short channel list under it.",
  "publicViewer.controls.io": "I/O",
  "publicViewer.controls.ioHint": "Show the channel and mix numbers on the plan.",
  "publicViewer.controls.more": "More",
  "publicViewer.controls.loadingWord": "(loading...)",
  "publicViewer.phantomTitle": "Phantom power (+48V)",
  "publicViewer.planFull.ariaLabel": "Open the plan full screen",
  "publicViewer.status.offerOwnRider": "You can't make an offer on your own rider.",
  "publicViewer.status.offerStartFailed": "Could not start the offer: {error}",

  // --- offer mode (pages/offer-overlay.ts, PR2 — docs/V2_OFFER_TECH_APPROVAL.md) -----------------------
  "offer.loading": "Loading offer…",
  "offer.loadFailed": "This offer could not be loaded: {error}",
  "offer.backToRider": "Back to rider",
  "offer.status.draft": "Draft",
  "offer.status.submitted": "Submitted",
  "offer.status.changesRequested": "Changes requested",
  "offer.status.awaitingOrganiser": "Awaiting organiser",
  "offer.status.approved": "Approved",
  "offer.roundLabel": "Answers v{n}",
  "offer.commentLabel": "General comment",
  "offer.commentPlaceholder": "Anything the rider's author should know about this offer as a whole…",
  "offer.authorCommentHeading": "Changes requested by the rider's author",
  "offer.declinedReasonHeading": "Reason for declining, from the rider's author",
  "offer.markRemainingOk": "Mark remaining OK",
  "offer.saveDraft": "Save draft",
  "offer.submit": "Submit offer",
  "offer.saving": "Saving…",
  "offer.savedStatus": "Draft saved.",
  "offer.submitting": "Submitting…",
  "offer.submitFailed": "Could not submit: {error}",
  "offer.saveFailed": "Could not save: {error}",
  "offer.printFailed": "Could not print: {error}",
  "offer.guest.notice": "You are not signed in. Your answers are kept in this browser until you send the offer.",
  "offer.myList.button": "Check against my warehouse",
  "offer.myList.empty": "Your warehouse is empty — add your stock in Warehouse",
  "offer.myList.link": "Warehouse",
  "offer.myList.summary": "From your list — can be marked OK: {ok}; you have fewer than needed: {short}; not in your list: {missing}",
  "offer.myList.apply": "Apply",
  "offer.myList.cancel": "Cancel",
  "offer.myList.short": "you have {have} of {need}",
  "offer.myList.marker": "from my list",
  "offer.myList.nothing": "Nothing to check: every gear line already has an answer.",
  "offer.guest.signInStep": "To send it, sign in or create an account — your answers are kept.",
  "offer.guest.signInButton": "Sign in or create account",
  "offer.guest.keepEditing": "Keep editing",
  "offer.guest.emailLabel": "Your email",
  "offer.guest.emailSend": "Send offer",
  "offer.guest.emailHint": "We will email you a link. Open it and the offer is sent — no password needed.",
  "offer.guest.emailInvalid": "Enter a valid email address.",
  "offer.guest.emailSent": "We sent a letter to {email}. Open the link in it — your offer will be sent by itself. You can close this tab; your answers are kept in this browser for 48 hours.",
  "offer.guest.emailSendFailed": "We could not send the letter right now. Please try again in a minute.",
  "offer.guest.emailAlreadySent": "The confirmation letter has already been sent to {email}. Open the link in it — your offer will be sent by itself. Check your Spam folder too: a letter from an unfamiliar sender often lands there. If it is not there, ask again in {minutes} min.",
  "offer.guest.emailSentAgain": "The confirmation letter has already been sent to {email}, and we sent it once more. Open the link in either letter — your offer will be sent by itself. Check your Spam folder too: a letter from an unfamiliar sender often lands there.",
  "offer.autoSending": "Sending your offer…",
  "offer.autoSent": "Your offer has been sent.",
  "offer.awaitingEmail": "We sent a letter to {email}. Open the link in it — your offer will be sent by itself.",
  "offer.where.title": "When and where — so the offer lands in the right show",
  "offer.where.dateLabel": "Date",
  "offer.where.kind.range": "Period",
  "offer.where.kind.choice": "Several dates",
  "offer.where.kind.day": "One day",
  "offer.where.pick.day": "Tap a day.",
  "offer.where.pick.range": "Tap the first day, then the last.",
  "offer.where.pick.choice": "Tap each date that could work — at least two.",
  "offer.where.clear": "No date yet",
  "offer.where.prevMonth": "Previous month",
  "offer.where.nextMonth": "Next month",
  "offer.where.chips": "Quick pick",
  "offer.where.weekdays": "Days that work",
  "offer.where.chip.thisWeek": "This week",
  "offer.where.chip.nextWeek": "Next week",
  "offer.where.chip.lastWeekOf": "Last week of {month}",
  "offer.where.chip.wholeMonth": "Whole month",
  "offer.where.possibleDays": "Possible days: {n}",
  "offer.where.removeDate": "Remove date",
  "offer.where.venue": "Venue",
  "offer.where.city": "City (optional)",
  "offer.where.hint": "The band sees your offer inside its show for this date.",
  "offer.where.error.dayMissing": "Pick the date.",
  "offer.where.error.rangeIncomplete": "Tap the last day of the period.",
  "offer.where.error.rangeBackwards": "The range ends before it starts.",
  "offer.where.error.rangeNoDays": "Every day of the range is crossed out — tick at least one weekday.",
  "offer.where.error.choiceFew": "Pick at least two dates, or switch to “One day”.",
  "offer.placed.whenVenue": "Your offer for this show ({when}, {venue}) has been sent. The show's organisers are reviewing it — thank you!",
  "offer.placed.when": "Your offer for this show ({when}) has been sent. The show's organisers are reviewing it — thank you!",
  "offer.placed.venue": "Your offer for this show ({venue}) has been sent. The show's organisers are reviewing it — thank you!",
  "offer.placed.none": "Your offer for this show has been sent. The show's organisers are reviewing it — thank you!",
  "offer.showLink.for": "For: {parts}.",
  "offer.showLink.archived": "This show is already agreed — it takes no more offers.",
  "offer.showLink.gone": "This show link does not work — it was withdrawn or could not be read. Ask the band for a new one.",
  "offer.send.title": "Send this offer?",
  "offer.send.rider": "Rider: {title}, v{version}",
  "offer.send.lines": "Lines: {total}",
  "offer.send.comment": "Your general comment",
  "offer.send.ask": "Please check: set the date and venue, or say that they are not set yet.",
  "offer.send.notSet": "Date and venue not set yet",
  "offer.send.notSetOn": "Going out as: date and venue not set yet.",
  "offer.send.or": "or",
  "offer.send.send": "Send",
  "offer.send.back": "Back to the offer",
  "offer.send.close": "Close",
  "offer.send.need": "Set a date and a venue, or tap “Date and venue not set yet”.",
  "offer.send.viaLink": "The date and venue come from the show link — there is nothing to fill in.",
  "offer.said.notSet": "Date and venue: not set yet (supplier's answer)",
  "riders.actions.publishToShow": "Publish to a show",
  "publishShow.title": "Publish to a show",
  "publishShow.hint": "A show has a date and a venue. A supplier who opens the link answers for exactly this show.",
  "publishShow.whenWhere": "When and where",
  "publishShow.rider": "Rider: {title}, v{version}",
  "publishShow.create": "Create show and link",
  "publishShow.creating": "Creating the show…",
  "publishShow.linkLabel": "Link for suppliers",
  "publishShow.copy": "Copy",
  // ОТПРАВИТЬ ССЫЛКУ (владелец, 14.10.2026). Канал назван им: «прокат идёт в воцап», «я сам посылаю
  // всё в воцап» — поэтому у кнопки не «скопировать», а «отправить», и список именно такой.
  "share.action": "Share",
  "share.hint": "Send this rider by email, WhatsApp, Telegram or any messenger",
  "share.email": "Email",
  "share.whatsapp": "WhatsApp",
  "share.telegram": "Telegram",
  "share.copyLink": "Copy link",
  "share.copied": "Link copied",
  "share.kind": "technical rider",
  "publishShow.copied": "Copied",
  "publishShow.close": "Close",
  "publishShow.notPublished": "Publish the rider first — a show points at a published version.",
  "publishShow.failed": "Could not create the show: {error}",
  "showPage.about": "About this show",
  "showPage.when": "Date",
  "showPage.venue": "Venue",
  "showPage.rider": "Rider",
  "showPage.riderValue": "{title}, v{version}",
  "showPage.dateOpen": "Date not agreed yet",
  "showPage.venueNone": "Venue not chosen yet",
  "showPage.makeOffer": "Make an offer",
  "showPage.archived.past": "This show took place. This page is its archive — it takes no more offers.",
  "showPage.archived.agreed": "This show is agreed. This page is its archive — it takes no more offers.",
  "showPage.repeat": "Repeat this show",
  "showPage.loadFailed": "Could not read this show: {error}",
  "repeatShow.title": "Repeat this show",
  "repeatShow.hint": "A new show with the same venue and acts. The original stays as it is.",
  "repeatShow.whenWhere": "New date",
  "repeatShow.rider.newer": "The rider has a newer version (v{newest}; played v{played})",
  "repeatShow.rider.quiet": "The rider has not changed since the show (v{played}).",
  "repeatShow.rider.newest": "Take the newest",
  "repeatShow.rider.keep": "Keep the played one",
  "repeatShow.rider.ask": "Ask the band for a new one",
  "repeatShow.offers.title": "Offers",
  "repeatShow.offers.count": "Approved offers on this show: {n}",
  "repeatShow.offers.invite": "Invite the same company again",
  "repeatShow.offers.none": "Start without offers",
  "repeatShow.create": "Create the new show",
  "repeatShow.creating": "Making the show…",
  "repeatShow.needAnswer": "Answer the questions above to continue.",
  "repeatShow.done": "The new show is made. This is its link for suppliers:",
  "repeatShow.failed": "Could not repeat the show: {error}",
  "emailLogin.title": "Signing you in",
  "emailLogin.checking": "Checking your link…",
  "emailLogin.failed": "This link has expired or was already used. Open the rider again and press Send — your answers are kept in this browser for 48 hours.",
  "emailLogin.noToken": "This link is incomplete. Open it from the letter again.",
  "emailLogin.success": "You are signed in.",
  "emailLogin.offerFailed": "An offer you sent with your email could not be delivered — the rider may have been withdrawn. Open the rider again and send it once more.",
  "emailLogin.usedTitle": "This link has already been used",
  "emailLogin.usedBody": "If you sent an offer with it, the offer went through — it is in the account for {email}. We will send a new link to the same address: it opens your account with your offer, and there you can set a password.",
  "emailLogin.usedBodyNoEmail": "If you sent an offer with this link, the offer went through. Enter your email and we will send a new link to your account.",
  "emailLogin.emailLabel": "Your email",
  "emailLogin.sendNew": "Send me a new link",
  "emailLogin.signIn": "I already have an account — sign in",
  "emailLogin.newSent": "We sent a new link to {email}. Open it — you will land in your account, next to your offer.",
  "setPassword.title": "Set a password to come back later",
  "setPassword.hint": "Your account was made by the link in your email. With a password you can open it any time and see your offer and the author's reply.",
  "setPassword.label": "New password (at least 8 characters)",
  "setPassword.button": "Set password",
  "setPassword.tooShort": "The password must be at least 8 characters.",
  "setPassword.done": "Password set. Next time, sign in with your email and this password.",
  "setPassword.failed": "Could not set the password. Please try again.",
  "offer.validation.modelRequired": "Enter a model for every \"Alt\" answer before saving.",
  "offer.validation.reasonRequired": "Enter a reason for every \"N/A\" answer before saving.",
  "offer.readOnlyNotice": "This offer is {status} and can no longer be edited.",
  "offer.table.answer": "Answer",
  "offer.bandBringsNote": "Band brings this",
  "offer.gear.consumables": "Consumables — from confirmed sets",
  "offer.gear.cablesElsewhere": "Cables are listed in their own section below.",
  "offer.notConnectedNote": "Not connected",
  "offer.answer.unanswered": "—",
  "offer.answer.ok": "OK",
  "offer.answer.markOk": "Confirm as OK",
  "offer.answer.substitute": "Alternative",
  "offer.answer.bandCarried": "Provided by band",
  "offer.answer.na": "Not available",
  "offer.answer.venueProvided": "Provided by venue",
  "offer.answer.modelPlaceholder": "Model",
  "offer.alt.warehouseEmpty": "Nothing of yours in this rubric",
  "offer.alt.searchCatalogue": "Search our catalogue",
  "offer.answer.reasonPlaceholder": "Reason",

  "offer.answer.commentPlaceholder": "Comment",
  "offer.answer.short.ok": "OK",
  "offer.answer.short.substitute": "Alt",
  "offer.answer.short.bandCarried": "Band",
  "offer.answer.short.na": "N/A",
  "offer.answer.short.venueProvided": "Venue",
  "offer.collapseAll": "Collapse all",
  "offer.expandAll": "Expand all",
  "offer.patchShow": "Patch this show →",
  "offer.confirmedOf": "{done} of {total} confirmed",
  "offer.summary.confirmed": "confirmed",
  "offer.summary.alternatives": "with an alternative",
  "offer.summary.unavailable": "not available",
  "offer.summary.open": "not answered yet",
  "offer.summary.nothingYet": "Nothing answered yet",
  "offer.allConfirmed": "Everything confirmed",
  "offer.sectionToggle": "Open or close this section",
  "offer.plan.title": "Stage plan",
  "offer.plan.io": "I/O",
  "offer.plan.ioShow": "Show channel and mix captions",
  "offer.plan.ioHide": "Hide channel and mix captions",
  "offer.plan.open": "Tap to open full screen",
  "offer.plan.close": "Close",
  "offer.plan.zoomIn": "Zoom in",
  "offer.plan.zoomOut": "Zoom out",
  "offer.plan.loading": "Drawing the stage plan…",
  "offer.plan.failed": "The stage plan could not be drawn.",
  "offer.wire.checkPatch": "Check the show patch and cable lengths →",
  "offer.wire.hint": "Opens the patch editor and cable solver",
  "offer.wire.longMic": "Long microphone cables",
  "offer.wire.shortPatch": "Short patch cables",
  "offer.detail.each": "Each of the {n} — what it is for",
  "offer.detail.kit": "kit {name}",
  "offer.detail.kitHide": "kit {name}",
  "offer.amp.summary": "{n} × LA4X amplified controller · {capacity} output channels · used {used} · free {free}",
  "offer.amp.capacity": "drive capacity per LA4X: 2 × 115XT HiQ or 4 × SB18 (L-Acoustics)",
  "offer.amp.channels": "{n} ch",
  "offer.amp.twoWay": "2-way active (LF + HF)",
  "offer.amp.oneChannel": "1 output channel",
  "offer.amp.used": "{used}/{capacity} ch",
  // --- offer review mode (pages/offer-review-overlay.ts, PR3 — docs/V2_OFFER_TECH_APPROVAL.md) -----
  "offer.review.supplierCommentLabel": "Supplier's comment",
  "offer.review.noComment": "No comment.",
  "offer.review.yourRequestHeading": "Changes you requested",
  "offer.review.approvedNotice": "This offer has been approved. No further changes are possible.",
  "offer.review.approve": "Approve",
  "offer.review.requestChanges": "Request changes",
  "offer.review.requestChangesCommentLabel": "What needs to change?",
  "offer.review.requestChangesCommentPlaceholder": "Tell the supplier what to fix before resubmitting…",
  "offer.review.sendRequest": "Send",
  "offer.review.approving": "Approving…",
  "offer.review.approveFailed": "Could not approve: {error}",
  "offer.review.sending": "Sending…",
  "offer.review.requestFailed": "Could not send: {error}",
  "offer.review.commentRequired": "Enter what needs to change before sending.",
  "offer.review.supplierNameUnknown": "Unknown supplier",
  "offer.review.historyHeading": "History",
  "offer.review.history.submitted": "Submitted",
  "offer.review.history.changesRequested": "Changes requested",
  "offer.review.history.approved": "Approved",
  "offer.status.declined": "Declined",
  "offer.review.decline": "Decline",
  "offer.review.declinedNotice": "This offer was declined. The supplier cannot change or resubmit it.",
  "offer.review.declineConfirm": "Decline this offer? This is final — the supplier will be told and cannot resubmit.",
  "offer.review.declineReasonLabel": "Reason (optional)",
  "offer.review.declineReasonPlaceholder": "Shown to the supplier in the email…",
  "offer.review.declineConfirmButton": "Decline offer",
  "offer.review.declining": "Declining…",
  "offer.review.declineFailed": "Could not decline: {error}",
  "offer.review.history.declined": "Declined",

  // --- /offers — the two-sided offer inbox (pages/offers-page.ts, PR3) -----------------------------
  "offers.pageTitle": "Offers",
  "offers.incomingHeading": "Offers on my riders",
  "offers.mineHeading": "My offers",
  "offers.incomingEmpty": "Nobody has answered one of your published riders yet.",
  "offers.mineEmpty": "You haven't made an offer on a rider yet.",
  "offers.table.rider": "Rider",
  "offers.table.supplier": "Supplier",
  "offers.table.supplierUnknown": "Unknown supplier",
  "offers.table.status": "Status",
  "offers.table.round": "Round",
  "offers.table.updated": "Updated",
  "offers.status.loading": "Loading…",
  "offers.status.loadFailed": "Could not load offers: {error}",

  // --- editor topbar (components/topbar.ts) -----------------------------------------------------------
  "topbar.loggedInAs": "logged in as {email}",
  "topbar.adminBadge": "admin",
  "topbar.adminBadgeTitle": "You have administrator privileges (e.g. Upload 3d object).",
  "topbar.unsavedRiderId": "(unsaved new rider)",
  "topbar.save.title": "Save Rider to database ({mod}+S)",
  "topbar.save.copyTitle":
    "Save as my own copy — this rider belongs to someone else, so saving stores a NEW rider under your account and leaves the original untouched ({mod}+S)",
  "topbar.save.copyLabel": "Save as my own copy",
  "topbar.saveAs.title": "Save as… — store the open rider as a NEW one under a name you choose, leaving the original untouched ({mod}+Shift+S)",
  "topbar.saveAs.label": "Save as…",
  "topbar.attach.title": "Attach object to another — arm, then click the object that should become its parent",
  "topbar.attach.label": "Attach object to another",
  "topbar.attach.armedTitle": "Attach mode armed — click the object in the 3D scene that should become the parent (Escape to cancel).",
  "topbar.attach.armedLabel": "Click a target…",
  "topbar.publish.label": "Publish rider",
  "topbar.publish.titleUnsaved": "Publish rider — save the rider first",
  "topbar.publish.titleNotMine": "Publish rider — only the owner can publish this rider",
  "topbar.publish.titlePublished": "Published as v{version} — public link: {url} (click to unpublish — takes down every version's link)",
  "topbar.publish.unpublishLabel": "Unpublish rider",
  "topbar.publish.titleDefault": "Publish rider — make it viewable at a public read-only link (for a QR code on the printed rider)",

  // --- editor page chrome (pages/editor-page.ts, EDITOR_PAGE_HTML template) ----------------------------
  "editor.riderIdTitle": "Saved rider id (blank = unsaved new rider)",
  "editor.menuToggleTitle": "Collapse/expand the menu",
  "editor.menuToggleLabel": "Menu",
  "editor.overflowMenuTitle": "More",
  "editor.rightPanelCollapseTitle": "Collapse panel",
  "editor.rightPanelExpandTitle": "Expand panel",
  "editor.rightPanelResizerTitle": "Drag to resize the panel (double-click to reset to its automatic width)",
  "editor.undoTitle": "Undo ({mod}+Z)",
  "editor.undoLabel": "Undo",
  "editor.redoTitle": "Redo ({mod}+Shift+Z)",
  "editor.redoLabel": "Redo",
  "editor.rotateTitle":
    "Rotate (R) — toggle the rotation ring; drag it to rotate. Default rotates the whole set (object + children); hold Alt to rotate just the object.",
  "editor.rotateLabel": "Rotate",
  "editor.attachArmedHint": "Click the object to attach “{title}” to — a podium, a stand · Esc to cancel",
  "editor.modifierAltHint": "Alt — only this object",
  "editor.modifierAltStackHint": "Alt — the whole stack",
  "editor.modifierShiftHint": "Shift — click objects to group, release to finish (or drop to attach)",
  "editor.uploadObjectTitleDisabled": "Upload 3d object — admin only",
  "editor.uploadObjectTitleAdmin": "Upload 3d object — create a new global preset from a .glb",
  "editor.uploadObjectLabel": "Upload 3d object",
  "editor.deleteObjectLabel": "Delete selected object",
  "editor.deleteObjectTitle": "Delete selected object (Alt+Delete)",
  "editor.savePresetLabel": "Save preset",
  "editor.deleteSubtreeLabel": "Delete object with all children",
  "editor.deleteSubtreeTitle": "Delete object with all children (Delete)",
  "editor.selectMode.groupLabel": "Whole stack",
  "editor.selectMode.groupTitle": "Selection: the whole stack — drag, rotate and delete take the group (hold Alt for one item)",
  "editor.selectMode.itemLabel": "One item",
  "editor.selectMode.itemTitle": "Selection: one item — drag, rotate and delete take the object you clicked (hold Alt for the whole stack)",
  "editor.deleteInGroupLabel": "Delete the whole stack",
  "editor.deleteInGroupTitle": "Delete the whole stack (Delete) — Alt: only the item you clicked",
  "editor.deleteInItemLabel": "Delete this item",
  "editor.deleteInItemTitle": "Delete the item you clicked (Delete) — Alt: the whole stack",
  "editor.deleteGroupConfirm.heading": "Delete the whole stack?",
  "editor.deleteGroupConfirm.body": "“{title}” will be removed from the stage together with everything standing on it. Items going: {n}.",
  "editor.deleteGroupConfirm.confirmLabel": "Delete the stack",
  "editor.showAnnotationsLabel": "Show/hide annotations",
  "editor.annotationOptionsTitle": "Annotation content options",
  "editor.annotationContentAriaLabel": "Annotation content",
  // The wide captions plaque's own top strip (SCRUM-403 v2, owner-chosen mock) — deliberately NOT
  // translated in any locale (see every other locale file's own copy of this key): it names the
  // control the way "LABELS" reads on a Pro Tools Edit Modes block, not a sentence to localize.
  "editor.labelsPlaqueStrip": "LABELS",
  "editor.annotationCh": "CH",
  "editor.topViewTitle": "Top view",
  "editor.topViewLabel": "Top view",
  "editor.planViewTitle": "Plan sheet",
  "editor.planViewLabel": "Plan sheet",
  // The view switcher's third and newest position (SCRUM-410) — deliberately NOT translated in any
  // locale (same reasoning as "LABELS" above): it names the render mode the way "3D" reads on any
  // 3D application's own UI, not a sentence to localize.
  "editor.view3dLabel": "3D",
  "editor.view3dTitle": "3D view",
  // The switcher's "some room" level (SCRUM-410, owner ask 27.09.2026: "когда место появляется, то
  // мы делаем подписи Top и Plan") — the owner named these two words himself, so — same as "3D" and
  // "LABELS" above — they are NOT translated in any locale.
  "editor.topViewShort": "Top",
  "editor.planViewShort": "Plan",
  "editor.viewSwitcherAriaLabel": "View",
  "editor.viewSwitcherOptionsTitle": "View options",
  "editor.planViewLoading": "Drawing the sheet…",
  "editor.planViewFailed": "The symbol library could not be loaded.",
  "editor.status.planMoveRefused": "This object cannot be moved there.",
  "presetMenu.editMode": "Edit",
  "presetMenu.editModeDone": "Done",
  "presetMenu.reorderFailed": "Could not save the new order: {error}",
  "presetMenu.setDefaultOrder": "Set as default order",
  "presetMenu.defaultOrderSaved": "This order is now the default for everyone who has not arranged their own.",
  "editor.annotationSource": "Source",
  "editor.annotationMix": "MIX",
  "editor.annotationTitle": "Title",
  "editor.annotationDesc": "Desc",
  "editor.showGridLabel": "Show/hide coordinate grid",
  "editor.printLabel": "Print rider",
  "editor.printTitle": "Export to PDF and print",
  "editor.exitLabel": "Exit to My Riders",
  "editor.riderOperationsAriaLabel": "Rider operations",
  "editor.tabs.properties": "Properties",
  "editor.tabs.propertiesTitle": "Properties (E)",
  "editor.tabs.rider": "Rider",
  "editor.tabs.inputList": "Input List",
  "editor.tabs.inputListTitle": "Input List (I)",
  "editor.tabs.outputList": "Output List",
  "editor.tabs.outputListTitle": "Output List (O)",
  "editor.tabs.gearList": "Gear List",
  "editor.riderTab.heading": "Rider",
  "editor.riderTab.titleLabel": "Title",
  "editor.riderTab.commentLabel": "Comment",
  "editor.riderTab.stageWidthLabel": "Width (m)",
  "editor.riderTab.stageDepthLabel": "Depth (m)",
  "editor.riderTab.useBorderLabel": "Use border of stage",
  "editor.riderTab.useBorderHint": "(off = allow objects outside the stage — pit, decor)",
  "editor.riderTab.contactLegend": "Contact & band",
  "editor.riderTab.moduleRider": "Rider",
  "editor.riderTab.moduleStage": "Stage",
  "editor.riderTab.moduleBand": "Band",
  "editor.riderTab.moduleContact": "Contact",
  "editor.riderTab.contactHint": "(pre-filled from your profile; saved with this rider)",
  "editor.riderTab.bandNameLabel": "Band name",
  "editor.riderTab.bandLogoAlt": "Band logo",
  "editor.riderTab.bandLogoFileLabel": "Band logo",
  "editor.riderTab.removeLogo": "Remove logo",
  "editor.riderTab.contactNameLabel": "Contact name",
  "editor.riderTab.contactPhoneLabel": "Contact phone",
  "editor.riderTab.contactEmailLabel": "Contact email",
  "editor.riderTab.saveDefaultsTitle":
    "Save the contact & band fields above as your personal defaults — they'll pre-fill every new rider you create",
  "editor.riderTab.saveDefaultsLabel": "Save these as my defaults",
  "editor.riderTab.riderTextLabel": "Rider text",
  "editor.riderTab.savedDefaultsStatus": "Saved as your defaults — new riders will start with this contact/band info.",
  "editor.riderTab.savedDefaultsFailed": "Could not save your defaults: {message}",
  "editor.listHint": "(whole rider — drag a row to reorder; Ch = its position. Click a row to select its object.)",
  "editor.reorderAriaLabel": "Reorder",
  "editor.table.ch": "Ch",
  "editor.table.source": "Source",
  "editor.table.type": "Type",
  "editor.table.chName": "ChName",
  "editor.table.micDi": "Mic / DI",
  "editor.table.out": "OUT",
  "editor.table.comment": "Comment",
  "editor.table.name": "Name",
  "editor.table.category": "Category",
  "editor.table.owner": "Owner",
  "editor.table.weight": "Weight",
  "editor.table.count": "Count",
  "editor.inputTab.heading": "Input List",
  "editor.inputTab.addButton": "Add input channel",
  "editor.outputTab.heading": "Output List",
  "editor.outputTab.addButton": "Add output line",
  "editor.gearTab.heading": "Gear List",
  "editor.gearTab.hint": "(whole rider — every object's own gear. Click a row to select its object.)",
  "editor.gearTab.addButton": "Add gear item",
  "editor.params.closeTitle": "Deselect / close",
  "editor.params.emptyHint": "Select an object on the stage to see its properties.",
  "editor.params.heading": "Selected object",
  "editor.params.nameLabel": "Name",
  "editor.params.nameHint": "(catalog key, read-only)",
  "editor.params.titleLabel": "Title",
  "editor.params.titleToSourceLabel": "Title to Source",
  "editor.params.commentLabel": "Comment",
  "editor.params.parentLabel": "Parent",
  "editor.params.xLabel": "X",
  "editor.params.yLabel": "Y",
  "editor.params.zLabel": "Z",
  "editor.params.zTitle": "Height above the stage (m). For decorative objects placed off the stage plane — e.g. a screen or drapes.",
  "editor.params.turnLabel": "Turn angle°",
  "editor.params.lockPositionLabel": "Lock position",
  "editor.params.lockChildrenLabel": "Lock children",
  "editor.params.structureLabel": "Structure",
  "editor.params.structureHint": "(objects can stand on it)",
  "editor.params.heightLabel": "Height, m",
  "editor.params.heightHint": "(surface height)",
  "editor.params.riserAcrossLabel": "Width, m",
  "editor.params.riserDeepLabel": "Depth, m",
  "editor.params.riserHeightsLabel": "Quick heights, m",
  "editor.params.riserResizeLabel": "Move and resize on stage",
  "presetMenu.riserTooltipHint": "double-click to resize",
  "editor.params.riserResizeDoneLabel": "Done",
  "editor.params.riserResizeHint": "Drag the object to move it, drag the handles to resize. Shift = free, Enter or Esc = done.",
  "editor.params.stageMoveLabel": "Move on stage",
  "editor.params.stageMoveHint": "Drag it on the stage to move it. Shift = free, Enter or Esc = done.",
  "editor.params.anchoredHint": "Anchored — double-click to move or resize",
  "editor.params.anchoredMoveHint": "Anchored — double-click to move",
  "editor.params.anchoredLockedHint": "Locked — clear “Lock position” to move it",
  "editor.params.snapToGridLabel": "Snap to grid",
  "editor.params.snapNeedsGridHint": "Snapping works only while the grid is visible (the grid button in the top bar). With the grid hidden, everything moves freely.",
  "editor.params.snapToGridHint": "Edges land on the grid of what the object is built from: the 0.5 m deck panel for a riser, the cabinet for an LED wall, the stock section for a truss. Off: it stays exactly where you put it.",
  "riser.handle.deep": "Drag to change the depth",
  "riser.handle.across": "Drag to change the width",
  "riser.handle.corner": "Drag to change width and depth",
  "riser.handle.height": "Drag to change the height (0.2 m steps)",
  "riser.handle.dock": "Add an identical riser on this side",
  "riser.readout": "{across} × {deep} m · H {height} m",
  "stage.handle.deep": "Drag to change the stage depth",
  "stage.handle.across": "Drag to change the stage width",
  "stage.handle.corner": "Drag to change the stage width and depth",
  "stage.readout": "Stage {across} × {deep} m",
  "riser.status.docked": "Added an identical riser beside this one.",
  "ledWall.handle.cols": "Drag to add or remove columns of cabinets",
  "ledWall.handle.rows": "Drag to add or remove rows of cabinets",
  "construction.led.cabinetLabel": "Cabinet, mm",
  "construction.led.cabinetCustom": "{w} × {h} (custom)",
  "construction.led.colsLabel": "Columns",
  "construction.led.rowsLabel": "Rows",
  "construction.led.pitchLabel": "Pixel pitch, mm",
  "construction.led.mountLabel": "Mount",
  "construction.led.mountGround": "Ground-stacked",
  "construction.led.mountFlown": "Flown",
  "construction.led.bottomLabel": "Bottom edge, m",
  "construction.led.contentLabel": "Face shows",
  "construction.led.contentOff": "Off (black)",
  "construction.led.contentGrid": "Cabinet grid",
  "construction.led.contentBars": "Colour bars",
  "construction.led.contentLogo": "Logo card",
  "construction.led.contentImage": "Image",
  "construction.led.contentMedia": "Media",
  "construction.led.chooseImage": "Choose image…",
  "construction.led.removeImage": "Remove image",
  "construction.led.imageSet": "Image set on the LED wall ({w} × {h} px).",
  "construction.led.imageTooBig": "This image is too large even after shrinking. Choose a smaller one.",
  "construction.led.imageUnreadable": "This file could not be read as an image.",
  "construction.led.imageRemoved": "Image removed from the LED wall.",
  "construction.led.mediaLabel": "Media",
  "construction.led.mediaLogo": "SoundCheckBox logo",
  "construction.led.mediaMascot": "Mascot",
  "construction.led.mediaIntro": "Intro",
  "construction.led.mediaIntroPlain": "Intro (no money)",
  "construction.led.mediaTutorial": "Tutorial video",
  "construction.led.mediaCustomUpload": "Uploaded: {name}",
  "construction.led.mediaCustomLink": "Link: {host}",
  "construction.led.mediaUrlLabel": "Direct link (https)",
  "construction.led.mediaUrlPlaceholder": "https://…/video.mp4",
  "construction.led.mediaUrlUse": "Show this link",
  "construction.led.mediaUpload": "Upload video or image…",
  "construction.led.mediaChecking": "Checking the link…",
  "construction.led.mediaUploading": "Uploading…",
  "construction.led.mediaSet": "The wall now shows your choice.",
  "construction.led.mediaProblemEmpty": "Paste a link to a video or picture file.",
  "construction.led.mediaProblemStreaming": "{site} pages cannot be shown on the wall: {site} plays video only inside its own player. Use a direct link to the video file (.mp4 or .webm), or upload the file.",
  "construction.led.mediaProblemNotHttps": "The link must start with https://",
  "construction.led.mediaProblemCredentials": "Remove the login and password from the link.",
  "construction.led.mediaProblemNotAddress": "This is not a web address. Paste the full link, which starts with https://",
  "construction.led.mediaProblemTooLong": "This link is too long.",
  "construction.led.mediaFailCors": "This site does not allow its video to be shown on other pages.",
  "construction.led.mediaFailUnreachable": "That address did not answer. Check the link and your connection.",
  "construction.led.mediaFailHttp": "That address answered with an error ({status}): the file is not there or is not public.",
  "construction.led.mediaFailNotMedia": "That address is a web page or a stream list, not a video or picture file. Use a direct link to an .mp4, .webm, .png, .jpg or .gif file.",
  "construction.led.mediaFailFormat": "The browser could not play this file. Use an mp4 (H.264) or webm video, or a png, jpeg or gif picture.",
  "construction.led.mediaUploadForbidden": "Only administrators can upload media.",
  "construction.led.mediaUploadType": "Only mp4, webm, png, jpeg and gif files can be put on a wall.",
  "construction.led.mediaUploadEmpty": "This file is empty.",
  "construction.led.mediaUploadTooBig": "This file is too large: videos up to {video} MB, pictures up to {image} MB.",
  "construction.led.mediaUploadFailed": "The upload failed. Try again.",
  "construction.led.derivedSize": "{size} m",
  "construction.led.derivedPx": "{px} px",
  "construction.led.derivedCount": "{count} cabinets",
  "construction.led.derivedLoad": "approx. {kg} kg · approx. {kw} kW max",
  "construction.truss.typeLabel": "Type",
  "construction.truss.typeLadder": "Ladder",
  "construction.truss.typeTriangle": "Triangle",
  "construction.truss.typeBox": "Box",
  "construction.truss.profileLabel": "Profile, mm",
  "construction.truss.lengthLabel": "Length, m",
  "construction.truss.trimLabel": "Trim (underside), m",
  "construction.truss.supportLabel": "Support",
  "construction.truss.supportFlown": "Flown (hoists)",
  "construction.truss.supportFloor": "On the floor",
  "construction.truss.supportGoalpost": "Goalpost (two towers)",
  "construction.truss.sections": "Sections: {sections}",
  "construction.truss.towers": "towers 2 × {h} m",
  "construction.truss.longSpan": "long span — check the load table",
  "editor.params.listsInputs": "Inputs",
  "editor.params.listsOutputs": "Outputs",
  "editor.params.listsGear": "Gear",
  "editor.params.moduleObject": "Object",
  "editor.params.modulePosition": "Position",
  "editor.params.listPosition": "position",
  "editor.params.listPositions": "positions",
  "editor.params.actionsLegend": "Object actions",
  "editor.params.rotateHint":
    "Rotate with the topbar <strong>Rotate</strong> tool (drag the ring; Shift = just the object), or type an exact angle in <strong>Turn angle</strong> above.",
  "editor.params.addChildLabel": "Add child",
  "editor.params.deleteObjectLabel": "Delete object",
  "editor.params.savePresetTitle": "Save this object and its children as a reusable preset",
  "editor.params.savePresetLabel": "Save as preset",
  "editor.params.addInputLabel": "Add new Input",
  "editor.params.addOutputLabel": "Add new Output",
  "editor.params.linkOutputLabel": "Link to output",
  "editor.params.linkOutputTitle": "Link this object to an existing output line or channel",
  "editor.params.linkOutputDisabledTitle": "No other outputs available on stage to link to",
  "editor.params.addGearLabel": "Add new Gear",
  "editor.presetCategoriesAriaLabel": "Preset categories",
  "editor.exitConfirm.title": "Leave without saving?",
  "editor.exitConfirm.body": "You have unsaved changes to this rider. If you leave now they will be lost.",
  "editor.exitConfirm.saveAndLeave": "Save and leave",
  "editor.exitConfirm.leaveWithoutSaving": "Leave without saving",

  // --- editor page behaviour (pages/editor-page.ts, JS-built status messages) ---------------------------
  "editor.status.gluedToGroup": "Joined the group — {members} objects now move together.",
  "editor.status.glueNoTarget": "Nothing under the drop point — hold Shift over an object to join its group.",
  "editor.status.glueFailed": "Could not join the group: {reason}",
  "editor.status.detached": "Taken out of its group — it moves on its own now.",
  "editor.status.detachNotGrouped": "This object is not in a group.",
  "editor.status.multiSelectCount": "{count} selected for grouping — release Shift to finish.",
  "editor.status.grouped": "Grouped {count} objects.",
  "editor.status.groupFailed": "Could not group: {reason}",
  "editor.status.attachCancelled": "Attach cancelled.",
  "editor.status.attachFailed": "Could not attach: {reason}",
  "editor.status.attached": "Attached {source} to {target}.",
  "editor.status.undo": "Undo.",
  "editor.status.redo": "Redo.",
  "editor.status.attachArmed": "Attach mode armed — click the object that should become the parent of {name} (Escape to cancel).",
  "editor.status.savingCopy": "Saving your own copy…",
  "editor.status.savedAsCopy":
    "Saved as a NEW rider — your own copy. The original belongs to someone else and is unchanged.",
  "editor.status.saved": "Saved at {time}.",
  "editor.status.guestDraftRestored": "Your work from last time is back — it lives in this browser until you save it to an account.",
  "editor.status.guestDraftDiscarded":
    "The work saved in this browser could not be opened safely, so it has been discarded rather than tried again. Starting from a new, empty rider.",
  "editor.status.guestDraftNotSaved":
    "Your browser could not save your changes here (storage may be full). Keep this tab open, or sign in and save to keep your work.",
  "editor.draft.offer": "Unsaved changes from {time}.",
  "editor.draft.continue": "Restore these changes",
  "editor.draft.discard": "Keep what is open",
  "editor.draft.close": "Close — decide later",
  "editor.naming.offer": "Saved. This rider is still called «New rider» and the band has no name — they are what you will look for later.",
  "editor.naming.name": "Name them now",
  "editor.naming.later": "Later",
  "editor.draft.offerDiverged": "You have unsaved changes from {time}, but this rider was saved again on the server after that. Restoring them over it would overwrite newer work.",
  "editor.draft.openAsCopy": "Open my changes as a copy",
  "editor.draft.useSaved": "Use the saved version",
  "editor.draft.restored": "Your unsaved changes are back. Press Save to keep them.",
  "editor.draft.restoredAsCopy": "Your changes are open as a new, unsaved rider. The saved original is untouched. Press Save to keep the copy.",
  "editor.draft.notKept": "Your browser could not keep a recovery copy of your changes (storage may be full). Press Save so they are not lost if you are signed out.",
  "editor.draft.crashed": "The unsaved changes kept in this browser could not be opened safely, so they were discarded rather than tried again.",
  "editor.status.modelsUnavailable": "Some 3D models could not be loaded — objects show as labelled boxes. Your work is safe.",
  "editor.status.presetsUnavailable": "The object catalogue could not be loaded — you can still draw, but the preset menu is empty. Reload to try again.",
  "editor.status.guestSaveNeedsAccount": "Sign in or sign up to keep this rider — your work is safe and comes with you.",
  "editor.status.guestSaveFailed": "Could not save it to your account: {message}. Your work is still here — press Save to try again.",
  "editor.status.saveFailed": "Save failed: {message}",
  "editor.saveAs.heading": "Save as a new rider",
  "editor.saveAs.nameLabel": "Name",
  "editor.saveAs.confirm": "Save a copy",
  "editor.saveAs.hint": "The rider you have open stays as it is — this saves a separate copy under the name you give it.",
  "editor.saveAs.untitled": "Untitled rider",
  "editor.status.savedAsNamedCopy": 'Saved "{title}" as a new rider. The rider you had open is unchanged.',
  "editor.status.unpublished": "Unpublished — every public link for this rider (this version and any earlier one) no longer works.",
  "editor.status.published": "Published as v{version} — public link: {url}",
  "editor.status.publishedNoPicture": "Published, but the plan picture could not be made — publish again without leaving this tab, and the reader will see the stage while the models load.",
  "editor.status.copiedSuffix": " (copied to clipboard)",
  "editor.status.publishFailed": "Publish failed: {message}",
  "publishProgress.publishing": "Publishing the rider…",
  "publishProgress.drawing": "Published as v{version}. Drawing the stage plan for the public page…",
  "publishProgress.done": "Published as v{version} — the public page is ready, stage plan included.",
  "publishProgress.published": "Published as v{version} — the public page is ready.",
  "publishProgress.noPicture": "Published as v{version}, but the stage plan could not be drawn. Readers will see the stage once its models load. Publish again without leaving this tab to add the picture.",
  "publishProgress.failed": "Publishing failed: {message}",
  "publishProgress.open": "Open the public page",
  "editor.status.ioKeptNote": " Its channel(s) were kept as empty/unconnected placeholders.",
  "editor.status.ioOptimizedNote": " Its channel(s) were removed and the numbering closed up.",
  "editor.status.deletedWithChildren": "Deleted {title}; {count} child object(s) kept and re-parented in place.",
  "editor.status.deleted": "Deleted {title}.",
  "editor.status.deletedSubtree": "Deleted {title} and all of its children.",
  "editor.status.rotated": "Rotated {name} by {deg}° ({mode}).",
  "editor.status.rotateModeObject": "object only; children held",
  "editor.status.rotateModeSet": "whole set",
  "editor.status.moved": "Moved {name} to X {x}, Y {y}.",
  "editor.status.dropMissed": "Drop missed the stage — try dropping closer to the platform.",
  "editor.status.loadingRider": "Loading the rider…",
  "editor.status.loaded": "Loaded \u201c{title}\u201d — {count} objects on the stage.",
  "editor.status.loadedNotMineSuffix":
    " This rider belongs to someone else — you can edit it freely, but Save will store your changes as your OWN new copy, leaving the original untouched.",
  "editor.status.loadFailed": "Load failed: {message}",
  "editor.status.newRider": "New rider (not saved yet).",

  // --- object-params panel behaviour (components/object-params-panel.ts) -------------------------------
  "objectParams.status.reparentFailed": "Could not reparent: {reason}",
  "objectParams.status.moved": "Moved {name} under {location}.",
  "objectParams.status.locationNewParent": "a new parent",
  "objectParams.status.locationTopLevel": "the top level",
  "objectParams.status.lockedChildren": "Locked position on all descendants of {name}.",
  "objectParams.status.reseated": "Re-seated {n} object(s) standing on {name}.",
  "objectParams.status.addedInput": "Added an input channel to {name}.",
  "objectParams.status.addedOutput": "Added an output line to {name}.",
  "objectParams.status.addedGear": "Added a gear item to {name}.",
  "objectParams.status.addedChild": "Added {childName} to {name}.",
  "objectParams.status.removed": "Removed {name}. The channels of the remaining objects are untouched.",
  "objectParams.noParentOption": "(no parent — top level)",

  // --- preset menu (components/preset-menu.ts) ----------------------------------------------------------
  "presetMenu.loading": "Loading presets…",
  // Shown instead of the line above once the fetch has actually given up (SCRUM-250 critic finding
  // 5) — "Loading presets…" is simply false at that point, and next to a status line that correctly
  // says the catalogue failed, the pair contradicted each other in the guest's very first view.
  "presetMenu.loadFailed": "Could not load the object catalogue. Reload to try again.",
  "presetMenu.countSuffix": "({count})",
  "presetMenu.searchPlaceholder": "Search the catalogue",
  "presetMenu.searchNoMatch": "Nothing matches “{q}”",
  "presetMenu.noMatch": "No presets match this filter.",
  "presetMenu.onlyMine": "Only mine",
  "presetMenu.added": 'Added preset "{name}" to the stage.',
  "presetMenu.removeFromCategory": "Remove from {category}",
  "presetMenu.removedFromCategory": "Removed {name} from {category}. The preset itself is untouched.",
  "presetMenu.removeFromCategoryFailed": "Could not remove {name} from {category}: {message}",
  "presetMenu.deleteLastCategoryPrompt": "Its only category. Deleting removes the preset for good — this cannot be undone. Delete?",
  "presetMenu.deleteAlsoFromPrompt": "Deletes the preset for good — it will also disappear from {others}, and this cannot be undone. Delete?",
  "presetMenu.deleting": 'Deleting preset "{name}"…',
  "presetMenu.deleted": 'Deleted preset "{name}".',
  "presetMenu.deleteFailed": 'Failed to delete preset "{name}": {message}',
  "presetMenu.uncategorizedCategory": "Uncategorized",
  "presetMenu.chooseCategoryPlaceholder": "— assign category —",
  "presetMenu.assignedCategory": 'Assigned preset "{name}" to category "{category}".',
  "presetMenu.assignCategoryFailed": 'Could not assign category to preset "{name}": {message}',

  // --- Patch Editor (components/patch-editor-dialog.ts) — SCRUM-241 ---------------------------------------
  "patchEditor.title": "Patch Editor & Stage Commutation",
  "patchEditor.subtitle": "Physical routing for {count} audio channel(s) across stage drop boxes",
  "patchEditor.stageBoxesHeading": "Digital StageBoxes & Primary Racks",
  "patchEditor.routingTableHeading": "Channel Commutation Routing",
  "patchEditor.colChannel": "Rider CH",
  "patchEditor.colSource": "Source / Line",
  "patchEditor.colSubSnake": "Sub-Snake / Drop Box",
  "patchEditor.colSocket": "Box Socket",
  "patchEditor.colStageBox": "Target StageBox",
  "patchEditor.colBoxInput": "Rack Input",
  "patchEditor.colCableLength": "Mic Cable",
  "patchEditor.cableSummaryHeading": "Cable Packing Summary (Truck / Trunk Spec)",
  "patchEditor.printSheet": "Print Patch Sheet",
  "patchEditor.close": "Close",
  "patchEditor.done": "Done",
  "topbar.patchEditor": "Patch",
  "topbar.patchEditor.disabledTooltip": "Patch Editor — Available in Show / Tour mode when a rider is attached to a venue with stageboxes & consoles.",
  "topbar.patchEditor.activeTooltip": "Open Patch Editor & Stage Commutation (P)",

  // --- I/O keep-on-delete dialog (components/io-delete-dialog.ts) ----------------------------------------
  "ioDelete.heading": "Keep the channel numbers, or close the gap?",
  "channelOverflow.heading": "Not enough free channels",
  "channelOverflow.summary": "“{preset}” needs {need} input channels. {free} are free ({slots}) — {short} short.",
  "channelOverflow.movingLine": "CH {ch} — {source} / {name}",
  "channelOverflow.movingMore": "{lines} … and {n} more",
  "channelOverflow.nothingToMove": "Nothing sits above the free channels — a shift moves nothing.",
  "channelOverflow.appendLabel": "Add at the end (CH {channels})",
  "channelOverflow.shiftLabel": "Shift from CH {from} up by {n}",
  "channelOverflow.cancelled": "“{name}” was not added — you cancelled the channel question.",
  "ioDelete.optimizeLabel": "Optimize I/O",
  "ioDelete.keepLabel": "Keep as is",
  "ioDelete.rememberLabel": "Don't ask again (remember this choice)",
  "ioDelete.inputChannelSingular": "{n} input channel",
  "ioDelete.inputChannelPlural": "{n} input channels",
  "ioDelete.outputChannelSingular": "{n} output channel",
  "ioDelete.outputChannelPlural": "{n} output channels",
  "ioDelete.summary": "The object you're removing carries {parts}.",

  // --- row-edit dialog (components/row-edit-dialog.ts) — docs/V2_IO_KEEP_ON_DELETE.md §3c --------------
  "rowEdit.headingInput": "Edit input channel",
  "rowEdit.headingOutput": "Edit output line",
  "rowEdit.sourceLabel": "Source",
  "rowEdit.chNameLabel": "Channel name",
  "rowEdit.typeLabel": "Type",
  "rowEdit.commentLabel": "Comment",
  "rowEdit.lockedLabel": "Locked",
  "rowEdit.chHashLabel": "Ch #",
  "rowEdit.lockedHint": "A locked line's channel can't change. Uncheck Locked to move it, then save — unlocking and moving happen together, in one save.",
  "rowEdit.lockedHintInput": "A locked channel's number can't change. Uncheck Locked to move it, then save — unlocking and moving happen together, in one save.",
  "rowEdit.linkedLabel": "Also connected (shared mix)",
  "rowEdit.linkedHint": "Other monitors fed by this same mix — e.g. two wedges on one send. They'll show this line's channel number on the stage plot too.",
  "rowEdit.linkedEmpty": "No other monitors on the stage yet.",
  "rowEdit.saveLabel": "Save",
  "rowEdit.deleteChannel": "Delete channel",
  "rowEdit.deleteLine": "Delete line",
  "rowEdit.errorInvalidChannel": "Channel must be a number, 1 or higher.",
  "rowEdit.errorChannelTaken": "Channel {channel} is already used by another line — pick a free number.",
  "rowEdit.errorLineGone": "This line no longer exists — close this dialog and try again.",
  "rowEdit.errorChannelGone": "This channel no longer exists — close this dialog and try again.",

  // --- link to existing output dialog (components/link-output-dialog.ts) ------------------
  "linkOutput.heading": "Link to existing output",
  "linkOutput.currentlyLinkedLabel": "Currently linked outputs:",
  "linkOutput.selectOutputLabel": "Select output line:",
  "linkOutput.chooseOutputOption": "Choose an existing output...",
  "linkOutput.manualOption": "Enter channel number manually...",
  "linkOutput.manualChannelLabel": "Channel number:",
  "linkOutput.submitLabel": "Link",
  "linkOutput.unlinkButtonTitle": "Unlink this output",
  "linkOutput.ownerSuffix": "owns this output",
  "linkOutput.alreadyLinkedSuffix": "already linked",
  "linkOutput.errorSelectChannel": "Please select an output or enter a valid channel number.",
  "linkOutput.error.invalidChannel": "Enter a channel number of 1 or higher.",
  "linkOutput.error.nodeNotFound": "That object is no longer on the stage. Close this and select it again.",
  "linkOutput.error.alreadyOwner": "This object already owns that output — there is nothing to link.",
  "linkOutput.error.channelNotFound": "There is no output on that channel yet. Create it with Add new Output first.",
  "linkOutput.errorGeneric": "Could not link to output.",
  "linkOutput.status.linked": "Linked {name} to Output {channel}",
  "linkOutput.status.unlinked": "Unlinked {name} from Output {channel}",

  // --- save-preset dialog (components/save-preset-dialog.ts) ---------------------------------------------
  "savePreset.heading": "Save as preset",
  "savePreset.nameLabel": "Preset name",
  "savePreset.categoryLabel": "Category",
  "savePreset.categoryRequiredHint": "(required)",
  "savePreset.saveAsLabel": "Save as",
  "savePreset.iconLegend": "Icon",
  "savePreset.renderingHint": "Rendering the object…",
  "savePreset.useRenderedIcon": "Use 3D snapshot",
  "savePreset.useOriginalIcon": "Use original preset icon",
  "savePreset.clearUploaded": "Clear uploaded file",
  "savePreset.submitLabel": "Save preset",
  "savePreset.usingUploadedImage": "Using your uploaded image.",
  "savePreset.usingOriginalPresetIcon": "Using original preset image — switch to 3D snapshot or upload a file if preferred.",
  "savePreset.autoGenerated": "Auto-generated from the 3D object — upload a file to override it.",
  "savePreset.iconPreviewAlt": "Preset icon preview",
  "savePreset.chooseCategoryOption": "— choose a category —",
  "savePreset.newPresetOption": "New preset",
  "savePreset.overwriteOption": "Overwrite: {title}",
  "savePreset.sharedLibrarySuffix": " (shared library)",
  "savePreset.confirmOverwriteButton": "Yes, overwrite",
  "savePreset.backToEdit": "Back",
  "savePreset.overwriteWarning":
    'Overwriting replaces "{title}" — every future insert of it will use your new version. Riders that already used it keep their own copy from when it was added, so they will not change.',
  "savePreset.overwriteWarningSharedNote": "This preset is part of the shared library — your update will be visible to everyone.",
  "savePreset.subject": "{title} — {count} object(s), saved with its whole subtree.",
  "savePreset.renderFailed": "Could not render an icon — the preset will save without one.",
  "savePreset.errorNodeGone": "The selected object is gone — close this dialog and select an object again.",
  "savePreset.errorNoCategory": "Pick a category — a preset without one would never appear in the menu.",
  "savePreset.errorStaleTarget": "You can no longer overwrite that preset — reopen this dialog and try again.",
  "savePreset.updated": 'Updated preset "{title}" (id {id}) — the change is live in your preset menu now.',
  "savePreset.saved": 'Saved preset "{title}" (id {id}) — it\'s in your preset menu now.',
  "savePreset.saveFailed": "Could not save the preset: {message}",
  "savePreset.imageReadFailed": "Could not read that image: {message}",

  // --- AI assistant dialog (components/ai-assistant-dialog.ts, docs/V2_AI_ASSISTANT.md) ----------
  "ai.buttonTitle": "AI assistant — describe a change in words",
  "ai.buttonLabel": "AI",
  "ai.heading": "AI assistant",
  "ai.subheading": "Describe a change to your stage in plain language. You review every edit before it is applied.",
  "ai.promptLabel": "Your request",
  "ai.promptPlaceholder": "e.g. add a drum kit upstage centre and a bass amp to its left",
  "ai.generate": "Generate",
  "ai.record": "Speak",
  "ai.recordTitle": "Speak your request (up to 20 s)",
  "ai.recording": "Recording… tap the mic to stop.",
  "ai.stopRecording": "Stop recording",
  "ai.micUnsupported": "Voice input is not supported in this browser.",
  "ai.micDenied": "Microphone access was denied — allow it in your browser to use voice.",
  "ai.micEmpty": "No audio was recorded — try again.",
  "ai.generating": "Thinking…",
  "ai.previewHeading": "Proposed changes",
  "ai.apply": "Apply changes",
  "ai.discard": "Discard",
  "ai.emptyPrompt": "Type what you would like to change first.",
  "ai.noChanges": "The assistant did not propose any changes. Try rephrasing your request.",
  "ai.notConfigured": "The AI assistant is not enabled on this server yet.",
  "ai.capReached": "You've used your free AI allowance. Add your own Gemini key in your profile to keep using the assistant.",
  "ai.providerError": "The AI service is temporarily unavailable. Please try again in a moment.",
  "ai.invalid": "The assistant could not produce a valid edit for this rider. Try rephrasing.",
  "ai.invalidReference": "The assistant referred to an object or preset that is not on this stage. Try naming it differently, or add it first.",
  "ai.someSkipped": "{count} change(s) couldn't be applied (they referenced something not on the stage, or didn't fit) and were skipped:",
  "ai.op.add": "Add an object",
  "ai.op.move": "Move an object",
  "ai.op.rotate": "Rotate an object",
  "ai.op.delete": "Delete an object",
  "ai.op.settings": "Update stage settings",
  "ai.op.unknown": "An operation",
  "ai.failed": "Could not reach the AI assistant. Check your connection and try again.",
  "ai.applied": "Applied {count} change(s) from the assistant.",
  "ai.diff.addPreset": "Add {preset} at ({x}, {y}) m",
  "ai.diff.moveObject": "Move {object} to ({x}, {y}) m",
  "ai.diff.rotateObject": "Rotate {object} by {deg}°",
  "ai.diff.deleteObject": "Delete {object}",
  "ai.diff.deleteTree": "Delete {object} and its {count} child object(s)",
  "ai.diff.setRiderMeta": "Update rider settings: {fields}",
  "ai.diff.metaField.title": "title",
  "ai.diff.metaField.comment": "comment",
  "ai.diff.metaField.width": "stage width",
  "ai.diff.metaField.length": "stage depth",

  // --- upload-object dialog (components/upload-object-dialog.ts) -----------------------------------------
  "uploadObject.heading": "Upload 3D object",
  "uploadObject.hint": "Admin-only. Creates a new global preset from a binary glTF (.glb) model.",
  "uploadObject.nameLabel": "Name",
  "uploadObject.modelLabel": "Model file (.glb)",
  "uploadObject.categoryLabel": "Category",
  "uploadObject.iconLabel": "Icon (optional)",
  "uploadObject.heightLabel": "Height, m",
  "uploadObject.structureLabel": "Is a structure (podium/riser objects can stack on it)",
  "uploadObject.submitLabel": "Upload & create preset",
  "uploadObject.errorNameRequired": "Name is required.",
  "uploadObject.errorModelRequired": "Choose a .glb model file.",
  "uploadObject.errorNotGlb": "Model must be a .glb file.",
  "uploadObject.errorNoCategory": "Pick a category.",
  "uploadObject.uploading": "Uploading…",
  "uploadObject.uploaded": 'Uploaded "{name}" — it\'s now in the preset library.',

  // --- list tables (components/list-tables.ts, editor/dom-utils.ts) --------------------------------------
  "dom.deleteLineTitle": "Delete this line",
  "dom.editLineTitle": "Edit this line",
  "dom.editChannelTitle": "Edit this channel",
  "dom.deleteChannelTitle": "Delete this channel",
  "listTables.unconnectedRowTitle":
    "Unconnected — this channel's object was deleted; the number is held open. Delete this row to free it.",
  "listTables.unconnectedChannelTitle": "Unconnected — this channel's object was deleted; the number is held open. Delete it to free the number.",
  "listTables.phantomTitle": "Phantom power (+48V) — click to toggle",
  "listTables.dragHandleTitle":
    "Drag to reorder — a row's channel number is its position in the list. Ctrl/Cmd- or Shift-click first to drag several rows together.",
  "listTables.clearSelectionTitle": "Click to clear the selection",
  "listTables.columnGripTitle": "{name} — drag the edge to resize, double-click the edge to fit",
  "listTables.panelGripTitle": "Drag to resize the panel — the same edge as the panel\u2019s own",
  "listTables.noneOption": "(none)",
  "listTables.categoryFromCatalogue": "The catalogue says what this is. To change it, change the model — not the label.",
  "listTables.categoryStale": "This line points at catalogue rubric {ref}, which the catalogue no longer has. Pick the model again.",
  "listTables.gearUngrouped": "Not part of a kit",
  "listTables.selectionBadge": "{count} selected — drag any of them to move the group",
  "listTables.linkLabel": "Link",
  "listTables.unlinkLabel": "Unlink",
  "listTables.pickModeLabel": "Select several",
  "listTables.linkTitle": "Link the two selected lines as a stereo pair",
  "listTables.unlinkTitle": "Unlink the selected pair (the names stay as they are)",
  "listTables.linkNeedTwo": "Select two neighbouring lines to link them as a stereo pair",
  "listTables.linkNotAdjacent": "Only neighbouring lines can be linked (channel N and N+1)",
  "listTables.linkAlreadyPaired": "One of these lines is already in a pair — unlink it first",
  "listTables.unlinkNothing": "Select a linked pair to unlink it",
  "listTables.pairMarkTitle": "Linked stereo pair — channels {a} and {b}",
  "ioDelete.pairSummary": "This line is one half of a stereo pair. The other half stays and becomes a single (mono) line.",
  "listTables.powerApprox": '≈{watts} W per unit — approximate catalog match ("{name}"), power draw may not be exact.',
  "listTables.powerExact": "{watts} W per unit, from the equipment catalog: {name}.",
  "listTables.powerLoading": "Loading equipment power data…",
  "listTables.powerError": "Equipment catalog unavailable — power total not calculated.",
  "listTables.notFoundInCatalog": "Not found in the catalog: {names}",

  // --- power summary (editor/power.ts — shared by the Gear tab AND the printed rider) --------------------
  "power.noGearItems": "No gear items yet.",
  "power.totalLine": "Stage / FOH: {backline} W · PA system: {pa} W",
  "power.fromLine": "— from {resolved} of {items}.",
  "picker.catalogLoading": "Loading the catalogue…",
  "picker.samePurpose": "same job",
  "picker.family": "related",
  "picker.startTyping": "Type a model or manufacturer",
  "picker.keepTyped": "Keep «{text}» as typed",
  "picker.classAnswers": "Models that answer «{name}»",
  "gear.notInCatalog": "not in catalogue",
  "power.itemSingular": "{n} item",
  "power.itemPlural": "{n} items",
  "power.zeroPowerSingular": "{items} draws no mains power.",
  "power.zeroPowerPlural": "{items} draw no mains power.",
  "power.unresolvedLine": "{items} not found in the catalog (not counted).",

  // --- print document (editor/print-rider.ts) -------------------------------------------------------------
  "printRider.noInputChannels": "No input channels.",
  "printRider.noOutputChannels": "No output channels.",
  "printRider.noGearItems": "No gear items.",
  "printRider.headers.ch": "Ch",
  "printRider.headers.v48": "48V",
  "printRider.headers.source": "Source",
  "printRider.headers.micDi": "Mic / DI",
  "printRider.headers.type": "Type",
  "printRider.headers.owner": "Owner",
  "printRider.headers.comment": "Comment",
  "printRider.headers.send": "Send",
  "printRider.headers.category": "Category",
  "printRider.headers.name": "Name",
  "printRider.headers.count": "Count",
  "printRider.headers.belongsTo": "Belongs to",
  "printRider.headers.offer": "OFFER",
  "printRider.gear.bandBrings": "The band brings this — do not order",
  "printRider.gear.allCables": "All cables required to connect the equipment listed in this rider",
  "printRider.gear.bandNote": "This gear will be on stage: the band brings it itself. Nothing here has to be supplied.",
  "printRider.gear.ungrouped": "Not part of a kit",
  "printRider.gear.noCategory": "No category",
  "gearList.section.pa": "P.A.",
  "gearList.section.amplification": "Amplification",
  "gearList.section.mixing": "Mixing system",
  "gearList.section.backline": "Backline",
  "gearList.section.monitoring": "Monitoring",
  "gearList.section.instruments": "Instruments",
  "gearList.section.mics": "Microphones and DI",
  "gearList.section.stands": "Stands",
  "gearList.section.stage": "Stage construction",
  "gearList.section.power": "Power distribution",
  "gearList.section.wire": "Cables",
  "gearList.section.consumables": "Consumables",
  "gearList.standFor.microphone": "Microphone stands",
  "gearList.standFor.guitar-bass": "Guitar and bass stands",
  "gearList.standFor.keyboard": "Keyboard stands",
  "gearList.standFor.wind": "Wind instrument stands",
  "gearList.standFor.drums-percussion": "Drum and percussion stands",
  "gearList.standFor.speaker": "Speaker stands",
  "gearList.standFor.sheet-music-other": "Music stands and other",
  "gearList.drumKit": "Drum kit",
  "gearList.otherInstruments": "Other instruments",
  "gearList.drumKitMics": "Drum kit mics",
  "gearList.otherMics": "Other mics and DI",
  "publicViewer.gear.eachFor": "Each of the {n} — what it is for",
  "gearList.bandBlock": "The band brings this",
  "gearList.where.standalone": "Standalone",
  "gearList.where.standaloneMany": "{count} standalone positions",
  "gearList.where.acrossStage": "{count} places across the stage",
  "gearList.alsoUnder": "also under {section}: {count}",
  "gearList.derivedFrom": "from input ch {channels} · {source} — change it in the Input list",
  "gearList.groupCount": "{lines} lines · {items} pcs",
  "gearList.groupCountOne": "1 line · {items} pcs",
  "gearList.unstated.pa": "Not specified in this rider.",
  "gearList.unstated.mixing": "Not specified in this rider — calculated from its input and output lists.",
  "gearList.unstated.band": "The band brings its own — listed at the end.",
  "gearList.mixing.need": "Input list: {inputs}. Output list: {outputs}.",
  "gearList.mixing.inputs.one": "{n} input",
  "gearList.mixing.inputs.few": "{n} inputs",
  "gearList.mixing.inputs.many": "{n} inputs",
  "gearList.mixing.inputs.other": "{n} inputs",
  "gearList.mixing.outputs.one": "{n} output",
  "gearList.mixing.outputs.few": "{n} outputs",
  "gearList.mixing.outputs.many": "{n} outputs",
  "gearList.mixing.outputs.other": "{n} outputs",
  "gearList.mixing.modelUnstated": "Model not specified.",
  "gearList.mixing.modelUnstatedBare": "model not specified",
  "gearList.mixing.requirement": "Mixing system with a capacity of at least {inputs} / {outputs}: {picks}.",
  "gearList.mixing.monitorRequirement": "Monitor console with a capacity of at least {inputs} / {outputs}: {picks}.",
  "gearList.mixing.pickBrand": "any {brand} desk",
  "mixing.line.title": "Mixing system",
  "mixing.monitorDesk.title": "Monitor desk",
  "mixing.monitorConsole.title": "Monitor console",
  "mixing.monitor.fromFoh": "From FOH",
  "mixing.monitor.separate": "Separate desk",
  "mixing.need.short": "≥{inputs} in / {outputs} out",
  "mixing.notSpecified": "Model not specified",
  "mixing.line.tooltip": "{title}: {picks} — click to edit",
  "mixing.dialog.need": "At least {inputs} inputs / {outputs} outputs (from the lists)",
  "mixing.dialog.close": "Close",
  "mixing.dialog.remove": "Remove {name}",
  "mixing.dialog.done": "Done",
  "mixing.dialog.digital": "Digital",
  "mixing.dialog.analogue": "Analogue",
  "mixing.dialog.brandsHint": "Pick a brand. Any desk of it that carries the show will do, or open it to choose exact desks.",
  "mixing.dialog.back": "‹ Brands",
  "mixing.dialog.anyBrand": "Any {brand} desk",
  "mixing.dialog.anyBrandMeta": "every one that carries the show: {fit} of {total}",
  "mixing.tier.senior": "Senior",
  "mixing.tier.mid": "Mid",
  "mixing.tier.entry": "Entry",
  "mixing.desk.figures": "{inputs} in / {outputs} out",
  "mixing.desk.unknown": "?",
  "mixing.desk.notStated": "capacity not stated",
  "mixing.desk.tooSmall": "too small for this rider",
  "mixing.desk.discontinued": "no longer made",
  "mixing.pick.brand": "Any {brand}",
  "gearList.bySection": "By section",
  "gearList.byKit": "By kit",
  "gearList.groupingLabel": "Group",
  "gearList.collapseAll": "Collapse all",
  "gearList.expandAll": "Expand all",
  "printRider.gear.orderSummary": "To supply: {lines} lines, {items} pcs in {sections} sections.",
  "printRider.gear.orderSummaryBand": "The band brings {lines} more lines itself: they are listed last and are not to be supplied.",
  "printRider.gear.youOffer": "You offer:",
  "printRider.headers.offerHint": "✓ as asked, or equivalent",
  "printRider.offer.date": "Date",
  "printRider.offer.city": "City",
  "printRider.offer.venue": "Venue",
  "printRider.offer.company": "Rental company",
  "printRider.offer.answeredBy": "Answered by",
  "printRider.offer.weAsk": "We ask",
  "printRider.offer.youOffer": "You offer",
  "printRider.gear.derivedFrom": "from the input list, ch {channels}",
  "printRider.gear.summaryLabel": "Summary",
  "printRider.gear.summaryOr": "or:",
  "printRider.gear.inDetail": "In detail",
  "printRider.gear.sum.wedgeMixes": "Floor-wedge mixes: {count}",
  "printRider.gear.sum.iemMixes": "In-ear mixes: {count}",
  "printRider.gear.sum.otherOutputs": "Other outputs: {count}",
  "printRider.gear.sum.microphones": "Microphones: {count}",
  "printRider.gear.sum.microphonesWireless": "Microphones: {count}, wireless among them: {wireless}",
  "printRider.gear.sum.diBoxes": "DI boxes: {count}",
  "printRider.gear.sum.drumKit": "Drum kit, pieces: {pieces}",
  "printRider.gear.sum.more": "and {count} more",
  "printRider.offer.heading": "This rider is answered in the document itself",
  "printRider.offer.howTitle": "How to answer.",
  "printRider.offer.howTick": "ticked — exactly as asked",
  "printRider.offer.howEq": "the line — the equivalent you offer",
  "printRider.offer.howSummary": "Each equipment section opens with a summary: tick it if the whole section is as asked, and answer line by line only where yours differs.",
  "printRider.offer.howNo": "Anything you cannot provide: write “no” and why. A reason is an answer; silence is not.",
  "printRider.offer.stageAsk": "Stage {width} × {length} m.",
  "printRider.offer.signatures": "Signatures",
  "printRider.offer.preparedBy": "Offer prepared by (rental company / venue)",
  "printRider.offer.acceptedBy": "Accepted for the band",
  "printRider.offer.signDate": "Date",
  "printRider.offer.answerOk": "OK",
  "printRider.offer.answerSub": "Alt: {model}",
  "printRider.offer.answerBandCarried": "Band-carried",
  "printRider.offer.answerNa": "N/A: {reason}",
  "printRider.offer.answerVenue": "Venue-provided",
  "printRider.offer.answerVenueNote": "Venue-provided: {note}",
  "printRider.offer.answeredHeading": "Offer by {supplier} · {status} · answers v{round}",
  "printRider.footer.offerFor": "Offer for: ______________________",
  "printRider.footer.page": "Page {page} of {pages}",
  "printRider.publicLinkMissing":
    'Public link: not published yet — use the editor\'s "Publish rider" button (top toolbar) to get a shareable, read-only link with a QR code here.',
  "printRider.publicLinkLabel": "Public link (scan or visit) — read-only, no login needed — v{version}, frozen at publish time",
  "printRider.qrUnavailable": "QR code unavailable (link too long to encode) — use the URL above.",
  "printRider.qrAriaLabel": "QR code linking to this rider's public page",
  "printRider.untitledRider": "Untitled rider",
  "printRider.stagePlotUnavailable": "Stage plot unavailable.",
  "printRider.stagePlotPending": "Taking the stage snapshot…",
  "printRider.stagePlanPending": "Building the stage plan…",
  "printRider.stagePlotAlt": "Stage plot",
  "printRider.sections.stagePlan": "Stage plan — top view",
  "printRider.stagePlanAlt": "Stage plan, top view",
  "printRider.stagePlanUnavailable": "Stage plan unavailable.",
  "printRider.contactLabel": "Contact: {name}",
  "printRider.telLabel": "Tel: {phone}",
  "printRider.emailLabel": "Email: {email}",
  "printRider.bandLogoAlt": "Band logo",
  "printRider.footerPage": "Page {page} of {pages}",
  "printRider.docTitle": "{heading} — rider",
  "printRider.metaBrand": "SoundCheckBox rider",
  "printRider.idLabel": "ID: {id}",
  "printRider.unsavedId": "(unsaved)",
  "printRider.stageSize": "Stage: {width} × {length} m",
  "printRider.sections.stagePlot": "Stage plot",
  "printRider.sections.riderNotes": "Rider notes",
  "printRider.sections.inputList": "Input list",
  "printRider.sections.outputList": "Output list",
  "printRider.sections.gearList": "Gear list",
  // --- print settings panel (components/print-options-dialog.ts, SCRUM-96) ---
  "printVariant.heading": "What do you want to print?",
  "printVariant.simpleName": "Simple View",
  "printVariant.simpleNote": "One landscape page: the stage plot with the input and output lists under it.",
  "printVariant.techName": "Tech stage plot",
  "printVariant.techNote": "One page, black-and-white plot — for the stage crew",
  "printVariant.bothName": "Both sheets",
  "printVariant.bothNote": "Two pages: black-and-white for the crew, colour for the artist",
  "printVariant.fullName": "Print all",
  "printVariant.fullNote": "The full rider, each section on its own sheet.",
  "print.fileNameSimple": "{title} — Simple View",
  "print.fileNameTech": "{title} — Tech Plot",
  "print.fileNameBoth": "{title} — Tech Plot + Simple View",
  "print.fileNameFull": "{title} — Full Details",
  "simpleView.captionSuffix": "See the interactive stage plot and make an offer on this rider at soundcheckbox.com",
  "simpleView.labelTech": "Tech stage plot",
  "simpleView.labelColour": "Simple view",
  "simpleView.captionUnpublished": "Unpublished rider — {site}",
  "editor.documentTitle": "{title} — SoundCheckBox",
  "editor.documentTitleUntitled": "Untitled rider",
  "simpleView.plotUnavailable": "Stage plot unavailable",
  "simpleView.overflow": "Some lines did not fit on this sheet.",
  "simpleView.textClipped": "The rest of the rider's text is in the full printed rider and on its public page.",
  "printOptions.heading": "Print settings",
  "printOptions.openTitle": "Print settings…",
  "printOptions.hint": "Choose what goes on the page. Leave a column heading empty to keep its standard name.",
  "printOptions.presetFull": "Full rider",
  "printOptions.presetOnePage": "One page (landscape)",
  "printOptions.gearLegend": "Equipment list",
  "printOptions.gearByCategory": "Group by category",
  "printOptions.gearByKit": "Group by kit",
  "printOptions.pageLegend": "Page",
  "printOptions.pageBreaks": "Each section on its own page",
  "printOptions.landscapePage": "Landscape page",
  "printOptions.alwaysLandscape": "Always landscape, full page",
  "printOptions.onCover": "On the cover",
  "printOptions.coverInfo": "Information",
  "printOptions.coverEditContacts": "Edit",
  "printOptions.coverSaveContacts": "Save",
  "printOptions.coverBandLink": "Band link",
  "printOptions.coverOff": "not printed",
  "printOptions.coverMissing": "Empty: {fields}",
  "printOptions.coverMissingShort": "empty: {count}",
  "printOptions.sectionsLegend": "Sections",
  "printOptions.lookLegend": "Look",
  "printOptions.coverLegend": "Cover",
  "printOptions.cover": "Print cover page",
  "printOptions.coverLogoSize": "Logo size",
  "printOptions.coverTextSize": "Text size",
  "printOptions.coverLogoFile": "Logo",
  "printOptions.coverNoLogo": "not uploaded",
  "printOptions.coverLogoUpload": "Upload",
  "printOptions.coverLogoReplace": "Replace",
  "printOptions.coverLogoClear": "Remove",
  "printOptions.coverLogoFromRider": "The logo lives in the rider — the same one appears on the public page and in the contact block",
  "printOptions.coverCheckEmpty": "empty",
  "printOptions.coverCheck.logo": "Logo",
  "printOptions.coverCheck.band": "Band",
  "printOptions.coverCheck.rider": "Rider",
  "printOptions.coverCheck.contact": "Contacts",
  "printOptions.coverCheck.link": "Band link",
  "printOptions.coverCheck.qr": "Public link QR",
  "printOptions.coverLogo": "Logo",
  "printOptions.coverText": "Text",
  "printOptions.align.left": "Left",
  "printOptions.align.center": "Center",
  "printOptions.align.right": "Right",
  "printOptions.previewTitle": "Preview",
  "printOptions.previewBuilding": "Building the preview…",
  "printOptions.previewFailed": "Preview unavailable",
  "printOptions.style.plain": "Plain",
  "printOptions.style.classical": "Classical",
  "printOptions.style.jazz": "Jazz",
  "printOptions.style.rock": "Rock",
  "printOptions.style.metal": "Metal",
  "printOptions.style.disco": "Disco",
  "printOptions.palette.mono": "Black and white",
  "printOptions.palette.graphite": "Graphite",
  "printOptions.palette.blue": "Blue",
  "printOptions.palette.red": "Red",
  "printOptions.palette.amber": "Amber",
  "printOptions.palette.violet": "Violet",
  "printOptions.inputColumnsLegend": "Input list columns",
  "printOptions.outputColumnsLegend": "Output list columns",
  "printOptions.confirm": "Print",
  "printOptions.savePdf": "Save as PDF",
  "printOptions.savedPdf": "PDF saved.",
  "printOptions.section.contact": "Band and contacts",
  "printOptions.section.publicLink": "Public link and QR",
  "printOptions.section.powerSummary": "Total power",
  "printOptions.section.stage3d": "Stage 3D view (in color)",
  "printOptions.section.techTopView": "Stage plot tech top view",

  // --- wysiwyg toolbar (components/wysiwyg-editor.ts) --------------------------------------------------
  "wysiwyg.bold": "Bold",
  "wysiwyg.italic": "Italic",
  "wysiwyg.underline": "Underline",
  "wysiwyg.bulletedList": "Bulleted list",
  "wysiwyg.numberedList": "Numbered list",
  "wysiwyg.clearFormatting": "Clear formatting",
  "wysiwyg.boldLabel": "B",
  "wysiwyg.italicLabel": "I",
  "wysiwyg.underlineLabel": "U",
  "wysiwyg.bulletedListLabel": "• List",
  "wysiwyg.numberedListLabel": "1. List",
  "wysiwyg.clearFormattingLabel": "⌫ Clear",

  // --- main.ts fatal startup overlay --------------------------------------------------------------------
  "main.fatal.heading": "SoundCheckBox failed to start",
  "main.fatal.hint": "Send this text to support — it identifies the problem exactly.",
  "main.fatal.userAgentLabel": "User agent: {ua}",

  // --- editor tour & cheatsheet (components/editor-tour.ts) ---------------------------------------------
  "tour.buttonTitle": "Interactive Tour & Help (?)",
  "tour.buttonLabel": "Tour",
  "tour.stepBadge": "Step {current} of {total}",
  "tour.closeAriaLabel": "Close",
  "tour.prev": "← Back",
  "tour.next": "Next →",
  "tour.finish": "Finish Tour",
  "tour.cheatSheetButton": "Cheat Sheet",
  "tour.cheatSheetButtonTitle": "View full keyboard & modifier cheat sheet",
  "tour.cheatSheetTitle": "Editor Shortcuts & Modifiers",
  "tour.startTourButton": "Start Tour",
  "tour.closeSheetButton": "Close",

  "tour.steps.stageSize.title": "1. Adjust the stage size",
  "tour.steps.stageSize.desc": "Double-click the stage floor (double-tap on a touch screen). Drag the handles on the edges and corners to set the stage width and depth; the size in metres shows at the top left. Click <b>Done</b> or press <b>Enter</b>.",
  "tour.steps.addObject.title": "2. Add members and equipment",
  "tour.steps.addObject.desc": "Pick a tab at the bottom (<b>members</b> for the musicians, <b>Drums &amp; Perc</b>, <b>mic &amp; DI</b>, <b>monitors</b>, <b>backline</b>…). Click a preset or drag it onto the stage, then drag it into position.",
  "tour.steps.addObject.iem": "Some musicians prefer in-ears.",
  "tour.steps.addObject.outputs": "The <b>Output List</b> recalculates automatically.",
  "tour.steps.members.title": "3. Members and their gear",
  "tour.steps.members.desc": "A member is the base of a preset: their instruments, mics and DIs come with them. Sometimes it is handier to see only the gear on stage: hold <b>Alt</b>, click the member and press <b>Delete</b> — the gear stays.",
  "tour.steps.arrange.title": "4. Arrange the stage",
  "tour.steps.arrange.desc": "Drag items into place. <b>Rotate</b> shows a ring: drag it to turn the selection. <b>Attach object to another</b>, then a click on a riser or a stand, fixes the item to it so they move together. <b>Whole stack</b> / <b>One item</b> sets what a click takes. Gestures: <b>Cheat Sheet</b>. The relative position of gear and stage structures is sometimes easier to fine-tune in <b>Top view</b>.",
  "tour.steps.channels.title": "5. Input List and Output List",
  "tour.steps.channels.desc": "The <b>Input List</b> and <b>Output List</b> are formed automatically from what stands on stage; just type the musicians' names in the <b>Source</b> column. Drag a row to change its <b>Ch</b>. Give the mixes their addresses: in <b>Source</b>, write who each monitor is placed for.",
  "tour.steps.micDi.title": "6. Mics and DIs",
  "tour.steps.micDi.desc": "Mics and DIs come with the presets. Click the <b>Mic / DI</b> cell in a row and pick the ones you prefer from the dropdown.",
  "tour.steps.planSheet.title": "7. Check the Plan sheet",
  "tour.steps.planSheet.desc": "Switch to <b>Plan sheet</b> and check how your rider looks as a drawing: this is how the crew on stage will see it. Items can be moved here too.",
  "tour.steps.riderFill.title": "8. Fill in the rider and save it",
  "tour.steps.riderFill.desc": "On the <b>Rider</b> tab on the right: write the <b>Rider text</b> and any notes, type the rider's <b>Title</b> and the <b>Band name</b>, add the <b>Band logo</b>. Then click <b>Save Rider to database</b> at the top.",
  "tour.steps.publishPrint.title": "9. Publish and print",
  "tour.steps.publishPrint.desc": "Once the rider is saved, <b>Publish rider</b> makes a read-only link for the venue and copies it. <b>PDF</b> asks <b>What do you want to print?</b>: <b>Simple View</b>, <b>Tech stage plot</b> or <b>Both sheets</b>; <b>Print all</b> opens <b>Print settings</b> with a preview.",
  "tour.end.title": "Clean the stage or continue?",
  "tour.end.desc": "The tour put equipment on the stage. Clean it away, or keep working on this stage?",
  "tour.end.clean": "Clean the stage",
  "tour.end.keep": "Continue with this stage",
  "tour.banner.text": "First time here? Take a short tour: the program shows each step on the stage.",
  "tour.banner.start": "Start the tour",
  "tour.banner.close": "Close",

  "tour.cs.sectionNav": "3D Navigation",
  "tour.cs.dragOrbit": "Rotate camera orbit",
  "tour.cs.dragPan": "Pan stage plane",
  "tour.cs.zoom": "Zoom in / out",
  "tour.cs.topView": "Toggle Top View / 3D",

  "tour.cs.sectionModifiers": "Gestures & Modifiers",
  "tour.cs.clickDefault": "Select & move whole group or podium",
  "tour.cs.altClick": "Select individual object inside group/podium",
  "tour.cs.altDrag": "Move individual object without moving group",
  "tour.cs.shiftGlue": "Glue / attach dragged object to target under cursor",
  "tour.cs.altShiftDetach": "Detach object from group in place",
  "tour.cs.rotateGizmo": "Turn group (hold Alt to turn single object on spot)",

  "tour.cs.sectionObjectOps": "Object Operations",
  "tour.cs.opProperties": "Open selected object parameters & audio channels",
  "tour.cs.opRotate": "Toggle interactive rotation ring (Gizmo)",
  "tour.cs.opAttach": "Arm attach mode, then click new parent object",
  "tour.cs.opSavePreset": "Save selected group/object as user preset",
  "tour.cs.opDelete": "Delete selected object (children re-parented)",
  "tour.cs.opDeleteTree": "Delete selected object and all its children",

  "tour.cs.sectionHotkeys": "Keyboard Shortcuts",
  "tour.cs.keyProperties": "Show / hide object properties",
  "tour.cs.keyRotate": "Toggle the rotation ring",
  "tour.cs.keyInputList": "Toggle Input List tab / window",
  "tour.cs.keyOutputList": "Toggle Output List tab / window",
  "tour.cs.keyUndo": "Undo last action",
  "tour.cs.keyRedo": "Redo action",
  "tour.cs.keySave": "Save rider",
  "tour.cs.keyEscape": "Deselect / Close dialog",
  "sideNav.shows": "Shows",
  "shows.pageTitle": "Shows",
  "shows.tab.inbox": "Inbox",
  "shows.tab.calendar": "Calendar",
  "shows.tabsLabel": "Shows sections",
  "shows.tab.list": "Shows",
  "shows.list.label": "All shows",
  "shows.list.col.date": "Date",
  "shows.list.col.band": "Band",
  "shows.list.col.venue": "Venue",
  "shows.list.col.city": "City",
  "shows.list.col.rental": "Rental",
  "shows.list.col.status": "Status",
  "shows.list.empty": "No shows yet.",
  "shows.list.emptyFiltered": "No shows on this selection.",
  "shows.list.rentalOne": "1 offer",
  "shows.list.rentalMany": "{n} offers",
  "shows.list.rentalNone": "No offers yet",
  "shows.list.rentalYou": "You",
  "shows.list.dateOpen": "Date not set yet",
  "shows.list.state.sketch": "Sketch",
  "shows.mcal.label": "Shows calendar",
  "shows.mcal.prev": "Previous month",
  "shows.mcal.next": "Next month",
  "shows.mcal.showAll": "Show all",
  "shows.mcal.filterDay": "Shows on {day}",
  "shows.mcal.filterMonth": "Shows in {month}",
  "shows.mcal.today": "today",
  "shows.loadFailed": "Could not load your shows: {error}",
  "shows.inbox.empty": "Nothing needs you right now.",
  "shows.inbox.listLabel": "Things that need you",
  "shows.inbox.unknownWho": "A supplier",
  "shows.inbox.approvalNew": "{who} sent an offer for {subject} ({when}). It waits for your approval.",
  "shows.inbox.approvalAgain": "{who} sent the offer for {subject} ({when}) again. It waits for your approval.",
  "shows.inbox.newOffer": "{who} sent an offer for {subject} ({when}).",
  "shows.inbox.reconfirm": "The rider changed (v{from} to v{to}). Re-confirm your offer for {subject} ({when}).",
  "shows.inbox.declined": "Your offer for {subject} ({when}) was declined.",
  "shows.inbox.declinedReason": "Reason: {reason}",
  "shows.inbox.riderRequest": "A rider was requested for {subject} ({when}).",
  "shows.inbox.open": "Open",
  "shows.inbox.dateOpen": "date not set yet",
  "shows.inbox.received": "Received {at}",
  "shows.inbox.new": "New",
  "sideNav.unreadCount": "{n} unread",
  "shows.cal.month": "Month",
  "shows.cal.week": "Week",
  "shows.cal.list": "List",
  "shows.cal.today": "Today",
  "shows.cal.prev": "Previous",
  "shows.cal.next": "Next",
  "shows.cal.noEvents": "No shows in this period",
  "shows.cal.waiting": "Waiting for a date",
  "shows.cal.waitingHint": "Shows with no date yet are not on the grid.",
  "shows.cal.waitingEmpty": "Nothing is waiting for a date.",
  "shows.cal.sketch": "Sketch",
  "shows.cal.untitled": "Untitled show",
  "shows.feed.title": "Subscribe in your calendar",
  "shows.feed.hint": "Google, Apple and Outlook calendars accept this address through «subscribe by URL».",
  "shows.feed.hidden": "Your link is on. For safety it is shown only when it is made — rotate it to get a new one.",
  "shows.feed.addressLabel": "Your private calendar address",
  "shows.feed.create": "Create link",
  "shows.feed.rotate": "Rotate",
  "shows.feed.rotateConfirm": "Click again: the old link stops working",
  "shows.feed.turnOff": "Turn off",
  "shows.feed.turnOffConfirm": "Click again to turn off",
  "shows.feed.failed": "Could not update the calendar link: {error}",
  "shows.detail.back": "Shows",
  "shows.detail.loadFailed": "Could not read this show: {error}",
  "shows.detail.state.open": "Open",
  "shows.detail.state.waiting": "Waiting for offers",
  "shows.detail.state.offers_in": "Offers in",
  "shows.detail.state.agreed": "Agreed",
  "shows.detail.state.past": "Past",
  "shows.detail.acts": "Performances",
  "shows.detail.noActs": "No act on this show yet.",
  "shows.detail.riderVersion": "Rider v{version}",
  "shows.detail.noRider": "Rider not here yet",
  "shows.detail.newerVersion": "Newer version available: v{version}",
  "shows.detail.getLink": "Show the show link",
  "shows.detail.linkFailed": "Could not get the link: {error}",
  "shows.detail.sketchTitle": "Sketch — no date, no venue yet",
  "shows.detail.sketchHint": "Set a date or a venue and it becomes an ordinary show.",
  "shows.detail.sketchWhere": "Date and venue",
  "shows.detail.change": "Change date and venue",
  "shows.detail.changeNeedsDate": "Pick a date — the show already has one.",
  "shows.detail.sketchSave": "Save date and venue",
  "shows.detail.sketchNeeds": "Choose a date or a venue first.",
  "shows.detail.saveFailed": "Could not save: {error}",
  "shows.offers.heading": "Offers",
  "shows.offers.empty": "No offers yet.",
  "shows.offers.sortLabel": "Sort by",
  "shows.offers.sort.time": "Time (submitted)",
  "shows.offers.sort.match": "Match",
  "shows.offers.sort.fewestMissing": "Fewest missing",
  "shows.offers.sort.price": "Price",
  "shows.offers.sort.priceNotYet": "not yet",
  "shows.offers.match": "{n} match",
  "shows.offers.missing": "{n} missing",
  "shows.offers.riderChanged": "Rider changed v{from} → v{to}",
  "shows.offers.open": "Open offer",
  "shows.offers.organiserApprove": "Approve as organiser",
  "shows.offers.organiserRequestChanges": "Request changes as organiser",
  "shows.offers.actionFailed": "Could not do that: {error}",
  // --- crew: the hired worker's screen and the admin's test link (SCRUM-762) ---
  "crew.untitledShow": "Show",
  "crew.dateOpen": "Date not set",
  "crew.venueNone": "Venue not set",
  "crew.signInWhy": "This is a job on a show. Sign in or create an account to take it — it takes a minute.",
  "crew.signInToTake": "Sign in to take this job",
  "crew.take": "Take this job",
  "crew.takenBySomeone": "Someone else has already taken this job. Ask whoever sent you the link for your own.",
  "crew.gone": "This job link does not exist or was withdrawn.",
  "crew.failed": "Something went wrong: {error}",
  "crew.saveFailed": "The tick was not saved: {error}",
  "crew.noRider": "This show has no published rider yet, so there is nothing to tick.",
  "crew.riderGone": "The rider of this show is not published any more, so the lists are empty. The steps are still here.",
  "crew.riderMoved": "The rider has been updated since this show was agreed — these lists are the new one.",
  "crew.riderMovedOn": "Rider updated {date} — newer than what was agreed for this show. These lists are the new one.",
  "crew.tab.out": "Prep",
  "crew.tab.venue": "Venue",
  "crew.tab.return": "Return",
  "crew.tab.plan": "Plan",
  "crew.time.warehouse": "Warehouse",
  "crew.time.truck": "Truck",
  "crew.time.venue": "Venue",
  "crew.time.showRun": "Show run",
  "crew.time.strike": "Strike",
  "crew.thisPart": "This part",
  "crew.doneOf": "{done} of {total}",
  "crew.done": "Done",
  "crew.twoPeople": "Two-person lift",
  "crew.showRunTwo": "2 people",
  "crew.kit.inside": "What's inside",
  "crew.kit.cablesApart": "Cables are counted in the Cables block",
  "crew.kit.included": "in the box",
  "crew.kit.consumablesApart": "Batteries and other consumables are counted on their own line",
  "crew.truckFront": "TRUCK FRONT",
  "crew.missing": "Missing",
  "crew.broken": "Broken",
  "crew.missingOpen": "MISSING — stays open",
  "crew.brokenOpen": "BROKEN — stays open",
  "crew.less": "One less",
  "crew.more": "One more",
  "crew.tickAria": "{name}: {done} of {total}",
  "crew.hint.fromOffer": "Not in this rider — comes from the rental's offer",
  "crew.hint.nothing": "Nothing here",
  "crew.returnOpen": "{n} lines still open (not back, missing or broken). They stay on the rental's list — nothing disappears.",
  "crew.returnAllBack": "Everything is back.",
  // SCRUM-760: «Loading done — depart» gate. The other five locales carry translations (crew-gate-locales.test.ts).
  "crew.gate.depart": "Loading done — depart",
  "crew.gate.departVenue": "Loaded — leave the venue",
  "crew.gate.confirm": "Truck leaves now? Press again",
  "crew.gate.waiting": "Waiting for the driver to depart",
  "crew.gate.departed": "Departed {time} · {name}",
  "crew.gate.departedNoName": "Departed {time}",
  "crew.gate.by.driver": "Sent by the driver",
  "crew.gate.by.supplier": "Sent by the rental company",
  "crew.gate.locked": "The truck has left — loading is closed. Ask the driver to reopen it if something was missed.",
  "crew.gate.next": "Go on — {tab}",
  "crew.gate.reopen": "Reopen loading",
  "crew.gate.failed": "Could not update the departure: {error}",
  "crew.zoom": "Zoom",
  "crew.fit": "Fit",
  "crew.legendDone": "done {n}",
  "crew.legendPart": "partly {n}",
  "crew.legendTodo": "to do {n}",
  "crew.legendMiss": "missing / broken {n}",
  "crew.planHint": "Ticks are set in blocks; the plan shows them by layer, in the order of the day.",
  "crew.changeover.title": "Changeovers between acts",
  "crew.changeover.heading": "Changeover {from} → {to}",
  "crew.changeover.headingMin": "Changeover {from} → {to} ({n} min)",
  "crew.changeover.none": "Nothing to move: what the next act needs already stands in place.",
  "crew.changeover.recheck": "Then line check, channels: {channels}",
  "crew.zone.USR": "Upstage right",
  "crew.zone.USC": "Upstage center",
  "crew.zone.USL": "Upstage left",
  "crew.zone.SR": "Stage right",
  "crew.zone.CS": "Center stage",
  "crew.zone.SL": "Stage left",
  "crew.zone.DSR": "Downstage right",
  "crew.zone.DSC": "Downstage center",
  "crew.zone.DSL": "Downstage left",
  "crew.step.toWingsStand": "Take {item} on its stand to the wings, cable stays on",
  "crew.step.rollOut": "Roll {item} out to the wings",
  "crew.step.back": "Bring {item} back from the wings",
  "crew.step.swapStand": "Take {from} off, put {to} on the same stand",
  "crew.step.replace": "Replace {from} with {to}",
  "crew.step.add": "Put up {item}",
  "crew.step.bandOut": "Clear {act}'s gear off the stage: {items}",
  "crew.step.bandIn": "Put up {act}'s gear: {items}",
  "crew.layer.picked": "Picked",
  "crew.layer.loaded": "Loaded",
  "crew.layer.placed": "Placed",
  "crew.layer.patched": "Patched",
  "crew.layer.checked": "Checked",
  "crew.layer.unpatched": "Unpatched",
  "crew.layer.struck": "Struck",
  "crew.layer.loadedback": "Loaded back",
  "crew.layer.returned": "Returned",
  "crew.phase.pick": "Pick at the warehouse",
  "crew.phase.pickSub": "Unload order — the truck is loaded in reverse",
  "crew.phase.load": "Load the truck",
  "crew.phase.loadSub": "Reverse order · heavy to the truck front",
  "crew.phase.setup": "Unload & set up",
  "crew.phase.setupSub": "At the venue",
  "crew.phase.showRun": "Show run",
  "crew.phase.showRunSub": "Crew stays on duty — stage tasks come later",
  "crew.phase.strike": "Strike & load out",
  "crew.phase.strikeSub": "After the show",
  "crew.phase.back": "Back at the warehouse",
  "crew.phase.backSub": "Unload and check in — what came back, what is missing, what is broken",
  "crew.block.carts": "Carts & ramp",
  "crew.block.racks": "Racks & power",
  "crew.block.pa": "P.A.",
  "crew.block.mon": "Monitors",
  "crew.block.foh": "FOH",
  "crew.block.mics": "Microphones",
  "crew.block.dis": "DIs",
  "crew.block.stands": "Stands",
  "crew.block.backline": "Backline",
  "crew.block.risers": "Risers & furniture",
  "crew.block.cables": "Cables",
  "crew.block.other": "Other",
  "crew.block.loadOut": "Load out · {block}",
  "crew.block.unloadToPositions": "Unload to positions",
  "crew.block.unloadToPositionsSub": "Heavy straight to place, FOH to its position",
  "crew.block.paMonitorsInPlace": "P.A. & monitors in place",
  "crew.block.powerPa": "P.A. power",
  "crew.block.powerStage": "Stage power",
  "crew.block.powerFoh": "FOH power",
  "crew.block.paCheck": "P.A. check & front fills",
  "crew.block.stageSet": "Stage set per rider",
  "crew.block.stageSetSub": "In parallel with the P.A. check",
  "crew.block.patch": "Patch",
  "crew.block.patchSub": "Every input of the input list",
  "crew.block.lineCheck": "Line check",
  "crew.block.lineCheckSub": "The band engineer arrives and starts the soundcheck",
  "crew.block.showRun": "Show run",
  "crew.block.showRunSub": "On stage during the show",
  "crew.block.showRunN": "Show run {n}",
  "crew.banner.powerOn": "Power on: sources first (console, stage rack, wireless), then the amps.",
  "crew.banner.powerOff": "Power off: amps first, then the sources (console, stage rack, wireless).",
  "crew.block.unpatch": "Unpatch",
  "crew.block.clearSmall": "Clear stage · small, light & cables",
  "crew.block.clearSmallSub": "Mics, DIs, stands, cables first",
  "crew.block.clearLarge": "Clear stage · large",
  "crew.block.clearLargeSub": "Amps, risers, piano, monitors",
  "crew.block.fohPaLast": "FOH & P.A.",
  "crew.block.fohPaLastSub": "Last off — first in at setup",
  "admin.crew.title": "Crew link for yourself (testing)",
  "admin.crew.hint": "Puts you on one of your shows as crew, to try the worker's screen from your phone. Open the link on the phone and take the job.",
  "admin.crew.show": "Show",
  "admin.crew.make": "Make crew link",
  "admin.crew.made": "Link made. Open it on your phone.",
  "admin.crew.link": "Crew link",
  "admin.crew.open": "Open",
  "admin.crew.noShows": "You have no shows yet — make one in Shows first",
  "admin.crew.dateOpen": "no date",
  "admin.crew.failed": "Could not do that: {error}",
  "admin.perf.title": "Add a performance to a show",
  "admin.perf.hint": "Makes it a multi-act show: the Venue tab of the crew link then shows the changeovers between the acts. Uses the show picked above.",
  "admin.perf.name": "Act name",
  "admin.perf.rider": "Rider (published)",
  "admin.perf.noRider": "No rider yet",
  "admin.perf.add": "Add performance",
  "admin.perf.added": "Added. The show now has {n} acts.",
  "admin.perf.failed": "Could not add it: {error}",
  "admin.perf.needName": "Give the act a name",

  // --- rental desk (rental/desk-answer-panel.ts, rental/desk-shows-panel.ts, pages/warehouse-tabs.ts, SCRUM-647) ---
  "rental.tabsLabel": "Warehouse sections",
  "rental.loadFailed": "Could not load: {error}",
  "rental.toAnswer.heading": "To answer",
  "rental.toAnswer.empty": "Nothing waits for you — every offer is answered.",
  "rental.cover.positions": "{n} positions",
  "rental.cover.positionsOne": "{n} position",
  "rental.cover.ok": "{n} from your warehouse",
  "rental.cover.short": "{n} short",
  "rental.cover.missing": "{n} missing",
  "rental.cover.answered": "answered {n}",
  "rental.cover.emptyWarehouse": "Your warehouse is empty — add gear to see coverage",
  "rental.reason.draft": "Not sent yet",
  "rental.reason.changes": "Changes asked",
  "rental.reason.reconfirm": "Rider changed — re-confirm",
  "rental.shows.heading": "My shows",
  "rental.shows.empty": "No shows yet. A show appears here once you send an offer for a date.",
  "rental.shows.dateOpen": "Date not set",
  "rental.shows.venueNone": "Venue not set",
  "rental.shows.myOffer": "My offer: {status}",
  "rental.crew.make": "Crew link",
  "rental.crew.making": "Making…",
  "rental.crew.newLink": "New crew link — send it to a worker",
  "rental.crew.linksHeading": "Crew links",
  "rental.crew.free": "Not taken yet",
  "rental.crew.takenBy": "Taken by {name}",
  "rental.crew.taken": "Taken",
  "rental.crew.failed": "Could not do that: {error}",
  "rental.crew.withdraw": "Withdraw",
  "rental.crew.withdrawSure": "Withdraw? Press again",
  "rental.crew.withdrawFailed": "Could not withdraw the link: {error}",
  // SCRUM-760: driver link, role on a link row, the departure line. The other five locales carry translations.
  "rental.crew.driver": "Driver link",
  "rental.crew.role.crew": "Crew",
  "rental.crew.role.driver": "Driver",
  "rental.crew.takeMyself": "Take it myself",
  "rental.crew.takenByYou": "Taken by you",
  "rental.crew.openMine": "Open",
  "rental.depart.out": "Left the warehouse",
  "rental.depart.venue": "Left the venue",
  "rental.depart.reopen": "Reopen loading",
  "rental.depart.failed": "Could not update the departure: {error}",
  "rental.progress.heading": "Work progress",
  "rental.tasks.heading": "What the crew does",
  "rental.tasks.blocks": "{n} blocks",
  "rental.tasks.template": "Our order of work",
  "rental.tasks.templateNote": "The same steps for every show. A show of its own adds the counts — what to pick, patch and check by the rider.",
  "rental.progress.doneOf": "{done} of {total}",
  "rental.progress.noLinks": "No crew link yet — progress shows once a worker ticks.",
  "rental.progress.noRider": "No published rider on this show — only the steps count.",
  "rental.progress.failed": "Progress could not load: {error}",
} as const;
